/**
 * Choreography for the logo easter egg. Each reaction is a scene: a fixed set
 * of sprites whose pose is a pure function of elapsed milliseconds. The stage
 * samples those functions into Animated interpolations (so it runs on the
 * native driver) and visual previews render any frame deterministically.
 *
 * Coordinates are stage pixels (the overlay above the page); a pose's x/y is
 * the sprite's centre. Every scene starts and ends with the carp exactly on
 * the logo, so swapping between the static logo and the stage is seamless.
 */
import { ENEMY_ART, FISH_POINTS, FISH_VIEWBOX, HELMET_BOX, pixelChunks, type CarpLook, type FishPart } from './logoEggArt'
import { isFireReaction, LOGO_FIRE_TARGETS, seededRandom, type LogoEnemy, type LogoReaction } from './logoEasterEgg'

export interface Pose { x: number; y: number; rotate: number; scaleX: number; scaleY: number; opacity: number }
export type SpriteKind =
  | { type: 'fish'; part: 'full' | FishPart }
  | { type: 'enemy'; enemy: LogoEnemy }
  | { type: 'pixel'; colour: string }
  | { type: 'bubble' }
  | { type: 'smoke'; colour?: string }
  | { type: 'drop' }
  | { type: 'flash' }
  | { type: 'burst'; colour: string }
  | { type: 'spark'; colour: string }
  | { type: 'note'; colour: string }
  | { type: 'hoop'; half: 'back' | 'front' }
  | { type: 'minnow'; colour: string; ears?: boolean }
  | { type: 'hat' }
  | { type: 'wand' }
  | { type: 'board' }
  | { type: 'disco' }
  | { type: 'carp'; look: CarpLook }
  | { type: 'sword' }
  | { type: 'heart'; colour: string }
  | { type: 'flower' }
  | { type: 'rocket' }
  | { type: 'parachute' }
  | { type: 'disguise' }
  | { type: 'barbell' }
  | { type: 'paperclip' }
  | { type: 'grenade' }
  | { type: 'wire' }
  /** `score`/`combo` sit on a dark outlined chip so they read over any page colour (SCORE_CHIP). */
  | { type: 'text'; text: string; style: 'score' | 'combo' | 'speech' | 'banner' | 'tip' }
export interface SceneSprite { id: string; kind: SpriteKind; width: number; height: number; pose: (ms: number) => Pose }
export interface LogoScene { reaction: LogoReaction; duration: number; sprites: SceneSprite[] }
export interface Rect { x: number; y: number; width: number; height: number }
export interface SceneLayout { stage: { width: number; height: number }; fish: Rect }
export interface SceneOptions {
  enemies?: readonly LogoEnemy[]; reducedMotion?: boolean
  /** Varies where the targets line up, and in which band (logoShotSeed); 0 by default (mid band), so frames are stable. */
  seed?: number
}

export const LOGO_SCENE_DURATIONS: Record<LogoReaction, number> = {
  'fire-puffer': 2400, 'fire-jelly': 2400, 'fire-shark': 2400, 'fire-angler': 2400, 'fire-crab': 2400,
  'fire-squid': 2400, 'fire-moray': 2400, 'fire-urchin': 2400, 'fire-seahorse': 2400, 'fire-double': 3000, 'fire-miss': 3000,
  'fire-combo': 3400,
  wink: 1300, flip: 1600, swim: 4400, bubbles: 2000, salute: 1700, shake: 1300, war: 1900, chase: 1900, invasion: 3900,
  dance: 3400, hula: 2400, juggle: 2600, magic: 2800, skate: 3200, sneeze: 2600,
  duel: 3000, waltz: 3200, rocket: 3000, balloon: 2600, disguise: 2400, lift: 2600, paperclip: 3400,
}
const REDUCED_WINK_DURATION = 1100
const SHOT_COLOUR = '#f2c14e'
/** Party colours: confetti, notes, floor lights. */
const PARTY = ['#f2c14e', '#e8508a', '#4fb3d9', '#7bc96f'] as const
const HIDDEN: Pose = { x: -9999, y: -9999, rotate: 0, scaleX: 1, scaleY: 1, opacity: 0 }

// ---- easing -----------------------------------------------------------------
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v))
const seg = (ms: number, a: number, b: number) => clamp((ms - a) / (b - a), 0, 1)
const lerp = (a: number, b: number, t: number) => a + (b - a) * t
const easeInOut = (t: number) => t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2
const easeOut = (t: number) => 1 - (1 - t) ** 3
const easeIn = (t: number) => t * t * t
const easeOutBack = (t: number) => 1 + 2.70158 * (t - 1) ** 3 + 1.70158 * (t - 1) ** 2
/** 0 → 1 → 0 across [a, b]. */
const bump = (ms: number, a: number, b: number) => Math.sin(Math.PI * seg(ms, a, b))
/** Deterministic 0..1 noise, so scenes (and their screenshots) are repeatable. */
const noise = (n: number) => { const v = Math.sin(n * 127.1 + 311.7) * 43758.5453; return v - Math.floor(v) }
const deg = (rad: number) => rad * 180 / Math.PI

function pose(x: number, y: number, extra: Partial<Pose> = {}): Pose {
  return { x, y, rotate: 0, scaleX: 1, scaleY: 1, opacity: 1, ...extra }
}

/** Fish-local offset (viewBox units from the carp's centre) to stage pixels. */
function fishPoint(p: Pose, unit: number, local: { x: number; y: number }) {
  const r = p.rotate * Math.PI / 180
  const lx = local.x * unit * p.scaleX, ly = local.y * unit * p.scaleY
  return { x: p.x + lx * Math.cos(r) - ly * Math.sin(r), y: p.y + lx * Math.sin(r) + ly * Math.cos(r) }
}

interface Ctx {
  layout: SceneLayout
  home: { x: number; y: number }
  /** Stage pixels per viewBox unit of the carp. */
  unit: number
  /** Size factor against the 36px phone header logo. */
  k: number
  W: number
  H: number
  sprites: SceneSprite[]
  /** Seeded 0..1 random for this play (target placement). */
  rng: () => number
  /** Where this play's shots land (shotBand for its tier). */
  band: Rect
}

function fishSprites(ctx: Ctx, fishPose: (ms: number) => Pose, lid: (ms: number) => number = () => 0, parts: 'full' | 'bare' = 'full') {
  const { width, height } = ctx.layout.fish
  // The sticker edge fades in as the carp grows past logo size, and is gone at rest.
  ctx.sprites.push({ id: 'fish-halo', kind: { type: 'fish', part: parts === 'bare' ? 'haloBare' : 'halo' }, width, height, pose: ms => {
    const p = fishPose(ms)
    return { ...p, opacity: clamp((Math.abs(p.scaleY) - 1) / 0.12, 0, 1) }
  } })
  ctx.sprites.push({ id: `fish-${parts}`, kind: { type: 'fish', part: parts }, width, height, pose: fishPose })
  ctx.sprites.push({ id: 'fish-eyelid', kind: { type: 'fish', part: 'eyelid' }, width, height, pose: ms => ({ ...fishPose(ms), opacity: clamp(lid(ms), 0, 1) }) })
}

/** The carp popping out of the logo: grows, mostly down/right so it stays on screen. */
function popOut(ctx: Ctx, ms: number, amount: number, start: number, end: number, duration: number) {
  const p = easeOutBack(seg(ms, start, start + end)) * (1 - easeInOut(seg(ms, duration - end, duration)))
  return { p, scale: 1 + amount * p, dx: ctx.layout.fish.width * amount * 0.3 * p, dy: ctx.layout.fish.height * amount * 0.55 * p }
}

function spark(ctx: Ctx, id: string, size: number, colour: string, at: (ms: number) => { x: number; y: number }, from: number, to: number, spin = 90) {
  ctx.sprites.push({ id, kind: { type: 'spark', colour }, width: size, height: size, pose: ms => {
    const s = seg(ms, from, to)
    if (s <= 0 || s >= 1) return HIDDEN
    const { x, y } = at(ms)
    return pose(x, y, { opacity: Math.sin(Math.PI * s), scaleX: 0.4 + 0.8 * Math.sin(Math.PI * s), scaleY: 0.4 + 0.8 * Math.sin(Math.PI * s), rotate: spin * s })
  } })
}

// ---- enemies and shots -----------------------------------------------------

function enemySize(ctx: Ctx, enemy: LogoEnemy) {
  const px = clamp(ctx.layout.fish.height / 10, 2.5, 4.5)
  const grid = ENEMY_ART[enemy].grid
  return { px, width: grid[0]!.length * px, height: grid.length * px }
}

export type ShotTier = 'upper' | 'mid'

/**
 * Which band a play's shots land in, from its seed (logoShotSeed): about half
 * the plays go high, just under the header line where the shots first landed,
 * the rest mid-screen. Seed 0 (previews, frames) is always mid.
 */
export function shotTier(seed: number): ShotTier {
  if (seed === 0) return 'mid'
  // Mix the seed (lowbias32) so neighbouring steps and plays don't move in lockstep.
  let h = seed >>> 0
  h = Math.imul(h ^ (h >>> 16), 0x7feb352d)
  h = Math.imul(h ^ (h >>> 15), 0x846ca68b)
  return (h ^ (h >>> 16)) & 1 ? 'upper' : 'mid'
}

/**
 * Where the shooting happens. Both bands start a few carp-widths right of
 * the logo so the gun never has to point straight down.
 * - `mid`: across the middle of the stage, well below the header (its
 *   buttons, and the desktop New Game button under it) and above the phone's
 *   pinned New Game footer.
 * - `upper`: upper centre, just below the header line. On wide screens it
 *   stops short of the New Game / Join Game buttons under the header's right
 *   end; score chips (SCORE_CHIP) are dark and outlined anyway, so they read
 *   over the paper, the canvas and the red button in both themes.
 */
export function shotBand(layout: SceneLayout, tier: ShotTier = 'mid'): Rect {
  const { width: W, height: H } = layout.stage
  const left = Math.max(W * 0.3, layout.fish.x + layout.fish.width * 3)
  if (tier === 'upper') {
    const right = W * (W >= 700 ? 0.55 : 0.78)
    // (The 20% caps only bite on the tiny preview stages.)
    return { x: left, y: layout.fish.y + layout.fish.height + Math.min(clamp(H * 0.08, 44, 90), H * 0.2), width: Math.max(0, right - left), height: Math.min(clamp(H * 0.09, 36, 80), H * 0.2) }
  }
  return { x: left, y: H * 0.34, width: Math.max(0, W * 0.78 - left), height: H * 0.22 }
}

/** A seeded spot in the play's band (fractions `rx`, `ry` of it), keeping a `halfWidth`-wide enemy on stage. */
function bandPoint(ctx: Ctx, rx: number, ry: number, halfWidth = 0) {
  const band = ctx.band
  return { x: clamp(band.x + band.width * rx, halfWidth + 12, ctx.W - halfWidth - 12), y: band.y + band.height * ry }
}

/** Where an enemy lines up for a shot: somewhere in the band, different each play. */
function firingTarget(ctx: Ctx, widthPx: number) {
  return bandPoint(ctx, ctx.rng(), ctx.rng(), widthPx / 2)
}

/**
 * Where the carp shoots from: it drops out of the header down the left side,
 * most of the way to its targets' height, so it fires across the middle.
 */
function firingSpot(ctx: Ctx, targets: readonly { x: number; y: number }[]) {
  const y = targets.reduce((sum, t) => sum + t.y, 0) / targets.length
  return { x: ctx.home.x + ctx.layout.fish.width * 0.25, y: lerp(ctx.home.y, y, 0.72) }
}

/** Each enemy's entrance, ending at `target` by `arrive` ms, then idling there. */
function enemySwim(ctx: Ctx, enemy: LogoEnemy, target: { x: number; y: number }, arrive: number, width: number) {
  const offX = ctx.W + width
  return (ms: number): Pose => {
    const s = seg(ms, 0, arrive)
    const idle = Math.sin(ms / 160)
    switch (enemy) {
      case 'puffer': {
        const x = lerp(offX, target.x, easeOut(s)), wave = Math.sin(2 * Math.PI * 2.5 * s)
        return pose(x, target.y + 9 * wave * (1 - s * 0.6) + 2 * idle, { rotate: 6 * Math.sin(2 * Math.PI * 2.5 * s + 1) })
      }
      case 'jelly': {
        const q = s * 4, step = Math.floor(q), f = q - step
        const prog = s >= 1 ? 1 : (step + easeOut(f)) / 4
        const squeeze = s >= 1 ? 0.05 * idle : Math.sin(Math.PI * f)
        return pose(lerp(target.x + 70, target.x, prog), lerp(-40, target.y, prog), { scaleX: 1 + 0.1 * squeeze, scaleY: 1 - 0.15 * squeeze })
      }
      case 'shark':
        return pose(lerp(offX, target.x, easeOutBack(seg(ms, 0, arrive * 0.7))), target.y + 1.5 * idle, { rotate: 5 * Math.sin(2 * Math.PI * 5 * s) * (1 - s) })
      case 'angler':
        return pose(lerp(offX, target.x, easeInOut(s)), lerp(target.y + 70, target.y, easeOut(s)) + 6 * Math.sin(2 * Math.PI * 1.5 * s), { rotate: 4 * Math.sin(2 * Math.PI * 1.5 * s + 0.5) })
      case 'crab':
        return pose(lerp(offX, target.x, easeOut(s)), lerp(target.y + 40, target.y, s) - 6 * Math.abs(Math.sin(2 * Math.PI * 4 * s)), { rotate: 10 * Math.sin(2 * Math.PI * 8 * s) * (1 - s) })
      case 'squid': {
        // Three jets down and in from the top right, stretching long on each.
        const q = s * 3, step = Math.floor(q), f = q - step
        const prog = s >= 1 ? 1 : (step + easeOut(f)) / 3
        const jet = s >= 1 ? 0 : Math.sin(Math.PI * f)
        return pose(lerp(offX, target.x, prog), lerp(target.y - 60, target.y, prog) + 1.5 * idle, { rotate: -50 * jet, scaleX: 1 - 0.15 * jet, scaleY: 1 + 0.2 * jet })
      }
      case 'moray':
        // Snakes up out of the deep.
        return pose(lerp(target.x + 40, target.x, easeOut(s)), lerp(ctx.H + width, target.y, easeOut(s)) + 1.5 * idle, { rotate: 22 * Math.sin(2 * Math.PI * 2 * s) * (1 - s) })
      case 'urchin': {
        // Rolls in along the band.
        const x = lerp(offX, target.x, easeOut(s))
        return pose(x, target.y, { rotate: deg((x - target.x) / (width / 2)) })
      }
      case 'seahorse':
        // Gallops in: three hops.
        return pose(lerp(offX, target.x, s), target.y - 26 * ctx.k * Math.abs(Math.sin(3 * Math.PI * s)) + 1.5 * idle, { rotate: -12 * Math.cos(3 * Math.PI * s) * (1 - s) })
    }
  }
}

/**
 * How each enemy goes down. Pixels fly out at `speed`, kicked up by `lift`,
 * fall with `gravity` and fade by `life` seconds; `jitter` 0 makes an even
 * ring (the pufferfish pops like a balloon).
 */
interface Boom { speed: number; jitter: number; lift: number; gravity: number; life: number; spin: number; burstScale: number; text: string }
export const ENEMY_BOOMS: Record<LogoEnemy, Boom> = {
  puffer: { speed: 300, jitter: 0.1, lift: 0, gravity: 40, life: 0.5, spin: 360, burstScale: 1.6, text: 'POP!' },
  jelly: { speed: 60, jitter: 0.8, lift: -20, gravity: 70, life: 0.95, spin: 120, burstScale: 1.1, text: 'ZAP!' },
  shark: { speed: 230, jitter: 1, lift: 90, gravity: 480, life: 0.7, spin: 720, burstScale: 1.9, text: '+500' },
  angler: { speed: 100, jitter: 1, lift: 0, gravity: 170, life: 0.85, spin: 240, burstScale: 1.2, text: 'LIGHTS OUT' },
  crab: { speed: 110, jitter: 0.7, lift: 270, gravity: 780, life: 0.75, spin: 900, burstScale: 1.3, text: '+200' },
  squid: { speed: 80, jitter: 0.9, lift: 30, gravity: 120, life: 0.9, spin: 200, burstScale: 1.3, text: 'INKED!' },
  moray: { speed: 150, jitter: 1, lift: 60, gravity: 300, life: 0.7, spin: 500, burstScale: 1.5, text: '+300' },
  urchin: { speed: 340, jitter: 0, lift: 0, gravity: 20, life: 0.45, spin: 0, burstScale: 1.4, text: 'OUCH!' },
  seahorse: { speed: 120, jitter: 0.6, lift: 200, gravity: 600, life: 0.8, spin: 600, burstScale: 1.2, text: 'NEIGH!' },
}
const INK_COLOUR = '#5a4aa8'
const ZAP_COLOUR = '#ffe066'

interface ShotPlan { id: string; enemy: LogoEnemy; at: number; target: (ms: number) => Pose; fishPose: (ms: number) => Pose; miss?: boolean; text?: string }
const FLIGHT = 140

/** Text that floats up from `at`, on a high-contrast chip sized to fit its label. */
function floatText(ctx: Ctx, id: string, text: string, at: { x: number; y: number }, from: number) {
  ctx.sprites.push({ id, kind: { type: 'text', text, style: 'score' }, width: (text.length * 9.5 + 20) * ctx.k, height: 20 * ctx.k, pose: ms => {
    const s = seg(ms, from, from + 740)
    if (s <= 0 || s >= 1) return HIDDEN
    return pose(at.x, at.y - 14 * ctx.k - 30 * ctx.k * easeOut(s), { opacity: seg(s, 0, 0.1) * (1 - seg(s, 0.6, 1)) })
  } })
}

function tracers(ctx: Ctx, id: string, from: { x: number; y: number }, to: { x: number; y: number }, at: number, flight: number) {
  const angle = deg(Math.atan2(to.y - from.y, to.x - from.x))
  for (const [n, delay] of [0, 45].entries()) {
    ctx.sprites.push({ id: `${id}-tracer${n}`, kind: { type: 'pixel', colour: SHOT_COLOUR }, width: 11 * ctx.k, height: 2.5 * ctx.k, pose: ms => {
      const s = seg(ms, at + delay, at + flight + delay)
      if (s <= 0 || s >= 1) return HIDDEN
      return pose(lerp(from.x, to.x, s), lerp(from.y, to.y, s), { rotate: angle })
    } })
  }
}

/** Muzzle flash, tracer, then (on a hit) burst, the enemy's own explosion, score; smoke either way. */
function addShot(ctx: Ctx, shot: ShotPlan) {
  const { id, enemy, at } = shot
  const hit = at + FLIGHT
  const muzzle = fishPoint(shot.fishPose(at), ctx.unit, FISH_POINTS.muzzle)
  const end = shot.target(hit)
  ctx.sprites.push({ id: `${id}-flash`, kind: { type: 'flash' }, width: 16 * ctx.k, height: 16 * ctx.k, pose: ms => {
    const s = seg(ms, at, at + 110)
    if (s <= 0 || s >= 1) return HIDDEN
    const m = fishPoint(shot.fishPose(ms), ctx.unit, FISH_POINTS.muzzle)
    const grow = Math.sin(Math.PI * s) * 1.3
    return pose(m.x + 4 * ctx.k, m.y, { scaleX: grow, scaleY: grow, rotate: 30 * s })
  } })
  for (let n = 0; n < 2; n++) {
    ctx.sprites.push({ id: `${id}-smoke${n}`, kind: { type: 'smoke' }, width: 9 * ctx.k, height: 9 * ctx.k, pose: ms => {
      const s = seg(ms, at + 100 + n * 90, at + 750 + n * 90)
      if (s <= 0 || s >= 1) return HIDDEN
      const m = fishPoint(shot.fishPose(ms), ctx.unit, FISH_POINTS.muzzle)
      const grow = 0.6 + 1.1 * s
      return pose(m.x + 6 * ctx.k * s, m.y - 16 * ctx.k * easeOut(s), { opacity: 0.55 * (1 - s), scaleX: grow, scaleY: grow })
    } })
  }
  if (shot.miss) {
    // The round sails on through where the enemy was, off the edge of the stage.
    const aim = shot.target(at)
    const dx = aim.x - muzzle.x, dy = aim.y - muzzle.y, len = Math.hypot(dx, dy) || 1
    const reach = Math.hypot(ctx.W, ctx.H)
    tracers(ctx, id, muzzle, { x: muzzle.x + dx / len * reach, y: muzzle.y + dy / len * reach }, at, FLIGHT * reach / len)
    floatText(ctx, `${id}-score`, 'MISS', aim, hit + 40)
    return hit
  }
  tracers(ctx, id, muzzle, end, at, FLIGHT)
  explode(ctx, id, enemy, end, hit, shot.text)
  return hit
}

/** An enemy going down at `end` at `hit` ms: hit glyph, its own pixels flying apart, its score. */
function explode(ctx: Ctx, id: string, enemy: LogoEnemy, end: { x: number; y: number }, hit: number, text?: string) {
  const art = ENEMY_ART[enemy]
  const boom = ENEMY_BOOMS[enemy]
  const { px, width, height } = enemySize(ctx, enemy)
  // The classic invader hit glyph, blinking where the enemy was.
  ctx.sprites.push({ id: `${id}-burst`, kind: { type: 'burst', colour: art.burst[0] }, width: 9 * px * boom.burstScale, height: 7 * px * boom.burstScale, pose: ms => {
    const s = seg(ms, hit + 20, hit + 300)
    if (s <= 0 || s >= 1) return HIDDEN
    const blink = Math.floor((ms - hit) / 60) % 2 ? 0.35 : 1
    return pose(end.x, end.y, { opacity: blink, scaleX: 1 + 0.4 * s, scaleY: 1 + 0.4 * s })
  } })
  // The enemy's own pixels fly apart, each enemy in its own way.
  const grid = art.grid, cx = grid[0]!.length / 2, cy = grid.length / 2
  pixelChunks(grid, art.palette).forEach((chunk, n) => {
    const dx = chunk.x - cx, dy = chunk.y - cy, len = Math.hypot(dx, dy) || 1
    const speed = boom.speed * (1 - boom.jitter * 0.45 + boom.jitter * 0.9 * noise(n + hit)) * ctx.k
    const vx = dx / len * speed, vy = dy / len * speed - boom.lift * ctx.k
    const colour = n % 3 === 2 ? art.burst[1] : chunk.colour
    ctx.sprites.push({ id: `${id}-bit${n}`, kind: { type: 'pixel', colour }, width: px * 2, height: px * 2, pose: ms => {
      const t = (ms - hit) / 1000
      if (t < 0.03 || t > boom.life) return HIDDEN
      return pose(end.x + dx * px + vx * t, end.y + dy * px + vy * t + boom.gravity * ctx.k * t * t,
        { opacity: 1 - seg(t, boom.life * 0.55, boom.life), rotate: (noise(n) - 0.5) * boom.spin * t })
    } })
  })
  floatText(ctx, `${id}-score`, text ?? boom.text, end, hit + 60)
  if (enemy === 'jelly') {
    // It discharges: sparks crackle around where it was.
    for (let n = 0; n < 5; n++) {
      const a = n * 2 * Math.PI / 5 + 0.4
      spark(ctx, `${id}-zap${n}`, (7 + 3 * (n % 2)) * ctx.k, ZAP_COLOUR, () => ({ x: end.x + Math.cos(a) * width * 0.6, y: end.y + Math.sin(a) * height * 0.6 }), hit + 20 + n * 50, hit + 220 + n * 50, 180)
    }
  } else if (enemy === 'shark') {
    // A big one: a second, wider flash at the impact.
    ctx.sprites.push({ id: `${id}-impact`, kind: { type: 'flash' }, width: 34 * ctx.k, height: 34 * ctx.k, pose: ms => {
      const s = seg(ms, hit, hit + 200)
      if (s <= 0 || s >= 1) return HIDDEN
      return pose(end.x, end.y, { scaleX: 0.6 + s, scaleY: 0.6 + s, opacity: 1 - s, rotate: -20 * s })
    } })
  } else if (enemy === 'angler') {
    // The lure's light drifts up and goes out.
    const lure = lurePoint(ctx, end)
    spark(ctx, `${id}-ember`, 9 * ctx.k, ENEMY_ART.angler.palette.l!, ms => ({ x: lure.x - 4 * ctx.k * seg(ms, hit, hit + 900), y: lure.y - 34 * ctx.k * easeOut(seg(ms, hit, hit + 900)) }), hit, hit + 900, 0)
  } else if (enemy === 'squid') {
    // It goes out in a cloud of its own ink.
    puffs(ctx, `${id}-ink`, 6, () => end, hit, width * 0.7, 14, INK_COLOUR)
  }
}

/** The angler's lure (top-left of its grid) for an angler posed at `p`. */
function lurePoint(ctx: Ctx, p: { x: number; y: number }) {
  const { px } = enemySize(ctx, 'angler'), grid = ENEMY_ART.angler.grid
  return { x: p.x + (1 - grid[0]!.length / 2) * px, y: p.y + (1 - grid.length / 2) * px }
}

/** An enemy that swims (or marches) in from `from` ms and is removed by the shot at `hit`. */
function addEnemy(ctx: Ctx, id: string, enemy: LogoEnemy, path: (ms: number) => Pose, hit: number, from = 0) {
  const { width, height } = enemySize(ctx, enemy)
  ctx.sprites.push({ id, kind: { type: 'enemy', enemy }, width, height, pose: ms => {
    if (ms <= from || ms >= hit + 50) return HIDDEN
    const p = path(ms)
    // A hit pops it: the pufferfish inflates, the rest flinch.
    const pop = seg(ms, hit, hit + 50)
    const grow = enemy === 'puffer' ? 1 + 0.5 * pop : 1 + 0.15 * pop
    return { ...p, scaleX: p.scaleX * grow, scaleY: p.scaleY * grow }
  } })
}

/**
 * What each enemy does once it has arrived and the carp takes aim: the
 * pufferfish puffs up, the jelly charges its sting, the shark lunges at the
 * carp, the angler sways behind its lure and the crab snaps its claws.
 */
/** How far the shark lunges at the carp: it waits that far back, so the hit still lands on its band spot. */
const sharkLunge = (ctx: Ctx) => 34 * ctx.k

function enemyAct(ctx: Ctx, enemy: LogoEnemy, swim: (ms: number) => Pose, arrive: number, hit: number) {
  return (ms: number): Pose => {
    const p = swim(ms)
    const t = seg(ms, arrive, hit)
    switch (enemy) {
      case 'puffer': {
        const puff = 1 + 0.32 * easeOutBack(seg(ms, arrive, arrive + 250)) + 0.03 * Math.sin(ms / 40) * t
        return { ...p, scaleX: p.scaleX * puff, scaleY: p.scaleY * puff }
      }
      case 'jelly': {
        const pulse = 0.08 * Math.sin(ms / 35) * t
        return { ...p, scaleX: p.scaleX * (1 + pulse), scaleY: p.scaleY * (1 - pulse) }
      }
      case 'shark': {
        // Nose down, it lunges at the carp: the carp fires point blank.
        const lunge = easeIn(t)
        return { ...p, x: p.x - sharkLunge(ctx) * lunge, rotate: p.rotate - 8 * lunge }
      }
      case 'angler':
        return { ...p, rotate: p.rotate + 6 * Math.sin(ms / 120) * t }
      case 'crab': {
        const snap = Math.abs(Math.sin(ms / 45)) * t
        return { ...p, x: p.x + 5 * ctx.k * Math.sin(ms / 70) * t, scaleY: p.scaleY * (1 - 0.08 * snap) }
      }
      case 'squid': {
        const wiggle = 0.1 * Math.sin(ms / 50) * t
        return { ...p, scaleX: p.scaleX * (1 - wiggle), scaleY: p.scaleY * (1 + wiggle) }
      }
      case 'moray':
        // Jaws snapping, it jabs at the carp.
        return { ...p, x: p.x - 8 * ctx.k * Math.abs(Math.sin(ms / 90)) * t }
      case 'urchin': {
        const bristle = 1 + 0.14 * Math.abs(Math.sin(ms / 60)) * t
        return { ...p, scaleX: p.scaleX * bristle, scaleY: p.scaleY * bristle }
      }
      case 'seahorse':
        // Rears up and paws the water.
        return { ...p, rotate: p.rotate + 14 * Math.sin(ms / 100) * t, y: p.y - 4 * ctx.k * Math.abs(Math.sin(ms / 100)) * t }
    }
  }
}

/** The angler's glowing lure while it is on stage. */
function addLure(ctx: Ctx, id: string, path: (ms: number) => Pose, from: number, until: number) {
  spark(ctx, id, 8 * ctx.k, ENEMY_ART.angler.palette.l!, ms => lurePoint(ctx, path(ms)), from, until, 0)
  // spark() fades in and out over its window; the lure should glow steadily, so re-shape it.
  const sprite = ctx.sprites[ctx.sprites.length - 1]!
  sprite.pose = ms => {
    if (ms <= from || ms >= until) return HIDDEN
    const p = lurePoint(ctx, path(ms)), glow = 0.75 + 0.25 * Math.sin(ms / 70)
    return pose(p.x, p.y, { opacity: glow, scaleX: glow, scaleY: glow })
  }
}

/**
 * The carp popping out with its gun and dropping to its firing `spot`: aims
 * at each shot in turn, recoils, lowers it, then climbs back into the logo.
 */
function gunnerPose(ctx: Ctx, duration: number, spot: { x: number; y: number }, shots: readonly { at: number; aim: number }[], { raise, lower, proud, flinch }: { raise: number; lower: number; proud?: number; flinch?: number }) {
  return (ms: number): Pose => {
    const pop = popOut(ctx, ms, 0.35, 0, 300, duration)
    const dive = easeInOut(seg(ms, 60, 560)) * (1 - easeInOut(seg(ms, duration - 520, duration - 40)))
    const bx = lerp(ctx.home.x + pop.dx, spot.x, dive), by = lerp(ctx.home.y + pop.dy, spot.y, dive)
    let aim = shots[0]!.aim * easeInOut(seg(ms, raise, raise + 400))
    for (let n = 1; n < shots.length; n++) aim = lerp(aim, shots[n]!.aim, easeInOut(seg(ms, shots[n - 1]!.at + 200, shots[n]!.at - 80)))
    aim *= 1 - easeInOut(seg(ms, lower, lower + 400))
    let recoil = 0
    for (const { at } of shots) if (ms >= at) recoil = Math.max(recoil, ms < at + 30 ? seg(ms, at, at + 30) : 1 - easeOut(seg(ms, at + 30, at + 240)))
    // A proud little hop once it is over; a frustrated shake after a miss.
    const hop = proud === undefined ? 0 : bump(ms, proud, proud + 250)
    const shake = flinch === undefined ? 0 : 7 * Math.sin((ms - flinch) / 28) * bump(ms, flinch, flinch + 320)
    const a = aim * Math.PI / 180
    return pose(bx - 7 * ctx.k * recoil * Math.cos(a), by - 7 * ctx.k * recoil * Math.sin(a) - 8 * ctx.k * hop,
      { rotate: aim - 8 * recoil + shake, scaleX: pop.scale, scaleY: pop.scale * (1 + 0.06 * hop) })
  }
}

/** Gun angle from the carp's muzzle at its firing `spot` to a point (the muzzle moves as it turns, so refine twice). */
function aimAt(ctx: Ctx, spot: { x: number; y: number }, point: { x: number; y: number }) {
  let angle = 0
  for (let n = 0; n < 3; n++) {
    const muzzle = fishPoint(pose(spot.x, spot.y, { scaleX: 1.35, scaleY: 1.35, rotate: angle }), ctx.unit, FISH_POINTS.muzzle)
    angle = clamp(deg(Math.atan2(point.y - muzzle.y, point.x - muzzle.x)), -35, 55)
  }
  return angle
}

/** Delays an entrance so it starts at `from` ms. */
const later = (path: (ms: number) => Pose, from: number) => (ms: number) => path(ms - from)

// ---- reactions ---------------------------------------------------------------

/** One enemy, one shot: each enemy enters, behaves and explodes in its own way. */
function fireAt(ctx: Ctx, enemy: LogoEnemy) {
  const duration = 2400
  const { width } = enemySize(ctx, enemy)
  const arrive = 1050, shotAt = 1150, hit = shotAt + FLIGHT
  const lunge = enemy === 'shark' ? sharkLunge(ctx) : 0
  const target = firingTarget(ctx, width + 2 * lunge)
  const path = enemyAct(ctx, enemy, enemySwim(ctx, enemy, { x: target.x + lunge, y: target.y }, arrive, width), enemy === 'shark' ? 800 : arrive, hit)
  const spot = firingSpot(ctx, [target])
  const fishPose = gunnerPose(ctx, duration, spot, [{ at: shotAt, aim: aimAt(ctx, spot, path(hit)) }], { raise: 450, lower: 1850, proud: 1550 })
  // It aims with one eye shut.
  fishSprites(ctx, fishPose, ms => seg(ms, 650, 710) - seg(ms, 1500, 1560))
  addEnemy(ctx, 'enemy', enemy, path, hit)
  if (enemy === 'angler') addLure(ctx, 'lure', path, 0, hit)
  addShot(ctx, { id: 'shot', enemy, at: shotAt, target: path, fishPose })
  return duration
}

/** Double tap: the jelly drops in and is shot, then the crab scuttles in and is shot. */
function fireDouble(ctx: Ctx, [first, second]: readonly LogoEnemy[]) {
  const duration = LOGO_SCENE_DURATIONS['fire-double']
  const a = first ?? 'jelly', b = second ?? 'crab'
  // Two spots in the band, one high, one low, a little apart.
  const targetA = bandPoint(ctx, 0.35 + 0.65 * ctx.rng(), 0.4 * ctx.rng(), enemySize(ctx, a).width / 2)
  const targetB = bandPoint(ctx, 0.65 * ctx.rng(), 0.6 + 0.4 * ctx.rng(), enemySize(ctx, b).width / 2)
  const shots = [{ at: 1000 }, { at: 1900 }]
  const pathA = enemyAct(ctx, a, enemySwim(ctx, a, targetA, 900, enemySize(ctx, a).width), 900, shots[0]!.at + FLIGHT)
  const enterB = 900
  const pathB = enemyAct(ctx, b, later(enemySwim(ctx, b, targetB, 850, enemySize(ctx, b).width), enterB), enterB + 850, shots[1]!.at + FLIGHT)
  const spot = firingSpot(ctx, [targetA, targetB])
  const aims = [{ at: shots[0]!.at, aim: aimAt(ctx, spot, pathA(shots[0]!.at + FLIGHT)) }, { at: shots[1]!.at, aim: aimAt(ctx, spot, pathB(shots[1]!.at + FLIGHT)) }]
  const fishPose = gunnerPose(ctx, duration, spot, aims, { raise: 400, lower: 2450, proud: 2250 })
  fishSprites(ctx, fishPose, ms => seg(ms, 600, 660) - seg(ms, 2200, 2260))
  addEnemy(ctx, 'enemy0', a, pathA, shots[0]!.at + FLIGHT)
  addEnemy(ctx, 'enemy1', b, pathB, shots[1]!.at + FLIGHT, enterB)
  if (a === 'angler') addLure(ctx, 'lure0', pathA, 0, shots[0]!.at + FLIGHT)
  if (b === 'angler') addLure(ctx, 'lure1', pathB, enterB, shots[1]!.at + FLIGHT)
  addShot(ctx, { id: 'shot0', enemy: a, at: shots[0]!.at, target: pathA, fishPose })
  addShot(ctx, { id: 'shot1', enemy: b, at: shots[1]!.at, target: pathB, fishPose })
  return duration
}

/** Miss, then hit: the shark ducks under the first round, then the carp gets it. */
function fireMiss(ctx: Ctx, enemy: LogoEnemy) {
  const duration = LOGO_SCENE_DURATIONS['fire-miss']
  const { width, height } = enemySize(ctx, enemy)
  const target = firingTarget(ctx, width)
  const miss = 1000, shotAt = 1900, hit = shotAt + FLIGHT
  const swim = enemySwim(ctx, enemy, target, 850, width)
  const path = (ms: number): Pose => {
    const p = swim(ms)
    // Ducks just as the trigger is pulled, bobs back up, then wiggles a taunt.
    const duck = easeOut(seg(ms, miss - 60, miss + 90)) * (1 - easeInOut(seg(ms, miss + 350, miss + 650)))
    const taunt = bump(ms, miss + 650, miss + 1050)
    return { ...p, y: p.y + duck * (height * 1.6 + 10 * ctx.k), rotate: p.rotate + 18 * duck + 10 * Math.sin(ms / 50) * taunt }
  }
  const spot = firingSpot(ctx, [target])
  const aim = aimAt(ctx, spot, target)
  const fishPose = gunnerPose(ctx, duration, spot, [{ at: miss, aim }, { at: shotAt, aim: aimAt(ctx, spot, path(hit)) }], { raise: 400, lower: 2450, proud: 2250, flinch: miss + 280 })
  fishSprites(ctx, fishPose, ms => seg(ms, 600, 660) - seg(ms, 2200, 2260))
  addEnemy(ctx, 'enemy', enemy, path, hit)
  // The miss aims where the shark was, not where it ducked to.
  addShot(ctx, { id: 'miss', enemy, at: miss, target: () => pose(target.x, target.y), fishPose, miss: true })
  addShot(ctx, { id: 'shot', enemy, at: shotAt, target: path, fishPose })
  return duration
}

function invasion(ctx: Ctx, enemies: readonly LogoEnemy[]) {
  const duration = LOGO_SCENE_DURATIONS.invasion
  const sizes = enemies.map(enemy => enemySize(ctx, enemy))
  const gap = 14 * ctx.k
  const rowWidth = sizes.reduce((sum, size) => sum + size.width, 0) + gap * (enemies.length - 1)
  // The row's first and last centres stay inside the band.
  const span = rowWidth - sizes[0]!.width / 2 - sizes[sizes.length - 1]!.width / 2
  const band = ctx.band
  const centre = bandPoint(ctx, clamp(span / 2 / (band.width || 1), 0, 0.5) + ctx.rng() * Math.max(0, 1 - span / (band.width || 1)), 0.1 + 0.6 * ctx.rng(), rowWidth / 2)
  const shots = [1300, 1950, 2600]
  // Space Invaders march: a jerky step in, then a shuffle in place.
  const offsets: number[] = []
  let left = -rowWidth / 2
  for (const size of sizes) { offsets.push(left + size.width / 2); left += size.width + gap }
  const formation = (ms: number) => {
    const steps = 11, s = Math.floor(seg(ms, 0, 1150) * steps) / steps
    const shuffle = ms > 1150 ? (Math.floor((ms - 1150) / 260) % 2 ? 5 : -5) * ctx.k : 0
    return { x: lerp(ctx.W + rowWidth / 2 + 20, centre.x, s) + shuffle, y: centre.y + (ms > 1150 ? Math.floor((ms - 1150) / 520) * 4 * ctx.k : 0) }
  }
  const paths = enemies.map((_, n) => (ms: number) => {
    const f = formation(ms)
    const legs = Math.floor(ms / 130) % 2
    return pose(f.x + offsets[n]!, f.y, { scaleY: legs ? 0.94 : 1 })
  })
  const spot = firingSpot(ctx, [centre])
  const aims = paths.map((path, n) => ({ at: shots[n]!, aim: aimAt(ctx, spot, path(shots[n]! + FLIGHT)) }))
  const fishPose = gunnerPose(ctx, duration, spot, aims, { raise: 500, lower: 3300 })
  fishSprites(ctx, fishPose, ms => seg(ms, 700, 760) - seg(ms, 3000, 3060))
  enemies.forEach((enemy, n) => {
    addEnemy(ctx, `enemy${n}`, enemy, paths[n]!, shots[n]! + FLIGHT)
    addShot(ctx, { id: `shot${n}`, enemy, at: shots[n]!, target: paths[n]!, fishPose })
  })
  ctx.sprites.push({ id: 'banner', kind: { type: 'text', text: 'WAVE CLEARED', style: 'banner' }, width: 164 * ctx.k, height: 28 * ctx.k, pose: ms => {
    const s = seg(ms, 3000, 3800)
    if (s <= 0 || s >= 1) return HIDDEN
    const blink = s < 0.45 && Math.floor((ms - 3000) / 90) % 2 ? 0.3 : 1
    return pose(centre.x, centre.y, { opacity: blink * (1 - seg(s, 0.8, 1)) })
  } })
  return duration
}

function wink(ctx: Ctx, reducedMotion: boolean) {
  if (reducedMotion) {
    // Nothing moves: the eye closes and a sparkle fades in and out.
    fishSprites(ctx, () => pose(ctx.home.x, ctx.home.y), ms => seg(ms, 250, 330) - seg(ms, 760, 840))
    ctx.sprites.push({ id: 'sparkle', kind: { type: 'spark', colour: '#f2c14e' }, width: 10 * ctx.k, height: 10 * ctx.k, pose: ms => {
      const p = fishPoint(pose(ctx.home.x, ctx.home.y), ctx.unit, { x: FISH_POINTS.eye.x + 2.2, y: FISH_POINTS.eye.y - 2.6 })
      return pose(p.x, p.y, { opacity: bump(ms, 280, 900) })
    } })
    return REDUCED_WINK_DURATION
  }
  const duration = LOGO_SCENE_DURATIONS.wink
  const fishPose = (ms: number) => {
    const pop = popOut(ctx, ms, 0.75, 0, 320, duration)
    const tilt = bump(ms, 420, 900)
    return pose(ctx.home.x + pop.dx, ctx.home.y + pop.dy, { rotate: -7 * pop.p + 9 * tilt, scaleX: pop.scale, scaleY: pop.scale })
  }
  fishSprites(ctx, fishPose, ms => seg(ms, 430, 490) - seg(ms, 860, 920))
  spark(ctx, 'sparkle', 12 * ctx.k, '#f2c14e', ms => fishPoint(fishPose(ms), ctx.unit, { x: FISH_POINTS.eye.x + 2.4, y: FISH_POINTS.eye.y - 2.6 }), 460, 920)
  return duration
}

function flip(ctx: Ctx) {
  const duration = LOGO_SCENE_DURATIONS.flip
  const { fish } = ctx.layout
  // The logo sits at the top of the screen, so the carp first drops onto a
  // "springboard" below it, then leaps up through a backflip and lands there,
  // before hopping back into the logo.
  const big = 1.3
  const drop = fish.height * 1.1
  const apex = Math.max(fish.height * big * 0.75 + 2, ctx.home.y - fish.height * 0.4)
  const jump = ctx.home.y + drop - apex
  const fishPose = (ms: number) => {
    const down = easeInOut(seg(ms, 0, 260)) * (1 - easeInOut(seg(ms, 1280, 1600)))
    const grow = 1 + (big - 1) * down
    const crouch = bump(ms, 240, 420), air = seg(ms, 380, 1040), land = bump(ms, 1020, 1260)
    const arc = Math.sin(Math.PI * air)
    const hop = bump(ms, 1280, 1600)
    return pose(ctx.home.x + 10 * ctx.k * arc, ctx.home.y + drop * down - jump * arc - 10 * ctx.k * hop + fish.height * 0.12 * (crouch + land), {
      rotate: -360 * easeInOut(air),
      scaleX: grow * (1 + 0.12 * crouch + 0.14 * land), scaleY: grow * (1 - 0.2 * crouch - 0.22 * land + 0.08 * arc),
    })
  }
  fishSprites(ctx, fishPose, ms => seg(ms, 560, 600) - seg(ms, 860, 900))
  // Dust puffs on landing.
  for (const side of [-1, 1]) {
    ctx.sprites.push({ id: `dust${side}`, kind: { type: 'smoke' }, width: 8 * ctx.k, height: 8 * ctx.k, pose: ms => {
      const s = seg(ms, 1040, 1420)
      if (s <= 0 || s >= 1) return HIDDEN
      const grow = 0.5 + s
      return pose(ctx.home.x + side * (fish.width * 0.45 + 14 * ctx.k * easeOut(s)), ctx.home.y + drop + fish.height * big * 0.42 - 4 * ctx.k * s, { opacity: 0.5 * (1 - s), scaleX: grow, scaleY: grow })
    } })
  }
  return duration
}

/** A closed Catmull-Rom tour of the screen, sampled by arc length for an even pace. */
function swimPath(ctx: Ctx) {
  const { W, H, home } = ctx
  const m = Math.max(ctx.layout.fish.width * 0.9, 40)
  const lo = { x: m, y: Math.max(m, home.y) }, hi = { x: W - m, y: H - m }
  const at = (fx: number, fy: number) => ({ x: clamp(fx, lo.x, hi.x), y: clamp(fy, lo.y, hi.y) })
  const points = [
    home,
    at(home.x + (W - home.x) * 0.35, home.y + H * 0.16),
    at(W - m, H * 0.4),
    at(W * 0.62, H - m * 1.4),
    at(W * 0.3, H * 0.74),
    at(m, H * 0.48),
    at(home.x + 24, home.y + H * 0.2),
    home,
  ]
  const curve = (u: number) => {
    const n = points.length - 1, i = Math.min(n - 1, Math.floor(u)), f = u - i
    const p0 = points[Math.max(0, i - 1)]!, p1 = points[i]!, p2 = points[i + 1]!, p3 = points[Math.min(n, i + 2)]!
    const c = (a: number, b: number, d: number, e: number) => 0.5 * (2 * b + (-a + d) * f + (2 * a - 5 * b + 4 * d - e) * f * f + (-a + 3 * b - 3 * d + e) * f * f * f)
    return { x: c(p0.x, p1.x, p2.x, p3.x), y: c(p0.y, p1.y, p2.y, p3.y) }
  }
  const samples = 400, lengths = [0]
  let prev = curve(0)
  for (let n = 1; n <= samples; n++) {
    const p = curve(n / samples * (points.length - 1))
    lengths.push(lengths[n - 1]! + Math.hypot(p.x - prev.x, p.y - prev.y)); prev = p
  }
  const total = lengths[samples]!
  return (s: number) => {
    const target = clamp(s, 0, 1) * total
    let lo = 0, hi = samples
    while (hi - lo > 1) { const mid = (lo + hi) >> 1; if (lengths[mid]! < target) lo = mid; else hi = mid }
    const span = lengths[hi]! - lengths[lo]!, f = span ? (target - lengths[lo]!) / span : 0
    return curve((lo + f) / samples * (points.length - 1))
  }
}

function swim(ctx: Ctx) {
  const duration = LOGO_SCENE_DURATIONS.swim
  const path = swimPath(ctx)
  const travel = (ms: number) => easeInOut(seg(ms, 250, duration - 250))
  const fishPose = (ms: number): Pose => {
    const s = travel(ms)
    const p = path(s), ahead = path(Math.min(1, s + 0.004)), behind = path(Math.max(0, s - 0.004))
    const vx = ahead.x - behind.x, vy = ahead.y - behind.y
    const moving = Math.hypot(vx, vy) > 0.01 && s > 0 && s < 1
    // Face the way it swims (flipping through a thin sliver), nose tilted along the path.
    const facing = moving ? Math.tanh(vx / 1.5) : 1
    const face = Math.abs(facing) < 0.25 ? Math.sign(facing || 1) * 0.25 : facing
    const tilt = moving ? clamp(deg(Math.atan2(vy, Math.abs(vx))), -35, 35) * Math.sign(face) : 0
    const grow = 1 + 0.4 * easeInOut(seg(ms, 0, 300)) * (1 - easeInOut(seg(ms, duration - 300, duration)))
    const swimming = seg(ms, 250, 500) * (1 - seg(ms, duration - 500, duration - 250))
    const wiggle = Math.sin(ms / 1000 * 2 * Math.PI * 3.2) * swimming
    return pose(p.x, p.y, { rotate: tilt + 7 * wiggle, scaleX: grow * face * (1 + 0.04 * wiggle), scaleY: grow })
  }
  fishSprites(ctx, fishPose, ms => seg(ms, duration - 650, duration - 600) - seg(ms, duration - 350, duration - 300))
  // A bubble trail from the tail.
  for (let n = 0; n < 16; n++) {
    const born = 400 + n * (duration - 1250) / 15
    const size = (5 + 5 * noise(n)) * ctx.k
    ctx.sprites.push({ id: `trail${n}`, kind: { type: 'bubble' }, width: size, height: size, pose: ms => {
      const s = seg(ms, born, born + 750)
      if (s <= 0 || s >= 1) return HIDDEN
      const origin = fishPoint(fishPose(born), ctx.unit, FISH_POINTS.tail)
      return pose(origin.x + 5 * ctx.k * Math.sin(s * 7 + n), origin.y - 26 * ctx.k * easeOut(s), { opacity: 0.85 * (1 - easeIn(s)), scaleX: 0.5 + 0.6 * s, scaleY: 0.5 + 0.6 * s })
    } })
  }
  return duration
}

function bubbles(ctx: Ctx) {
  const duration = LOGO_SCENE_DURATIONS.bubbles
  const fishPose = (ms: number) => {
    const pop = popOut(ctx, ms, 0.35, 0, 260, duration)
    const puff = Math.max(0, Math.sin((ms - 300) / 150 * Math.PI)) * seg(ms, 300, 320) * (1 - seg(ms, 1350, 1400))
    return pose(ctx.home.x + pop.dx, ctx.home.y + pop.dy + 2 * ctx.k * Math.sin(ms / 140) * pop.p, { rotate: -6 * pop.p, scaleX: pop.scale * (1 + 0.05 * puff), scaleY: pop.scale * (1 + 0.03 * puff) })
  }
  fishSprites(ctx, fishPose, ms => seg(ms, 1450, 1500) - seg(ms, 1650, 1700))
  const count = 7
  for (let n = 0; n < count; n++) {
    const born = 300 + n * 150, last = n === count - 1
    const life = last ? 700 : 650 + 120 * noise(n + 3)
    const size = (last ? 17 : 6 + 6 * noise(n)) * ctx.k
    const rise = (34 + 30 * noise(n + 9)) * ctx.k
    ctx.sprites.push({ id: `bubble${n}`, kind: { type: 'bubble' }, width: size, height: size, pose: ms => {
      const s = seg(ms, born, born + life)
      if (s <= 0 || s >= 1) return HIDDEN
      const mouth = fishPoint(fishPose(born), ctx.unit, FISH_POINTS.mouth)
      const popping = seg(s, 0.88, 1)
      const grow = (0.35 + 0.65 * easeOut(seg(s, 0, 0.4))) * (1 + 0.5 * popping)
      return pose(mouth.x + (14 + 10 * noise(n + 5)) * ctx.k * easeOut(s) + 3 * ctx.k * Math.sin(s * 9 + n), mouth.y - rise * easeOut(s) + (last ? 10 * ctx.k * s : 0),
        { opacity: 0.9 * (1 - popping), scaleX: grow, scaleY: grow })
    } })
  }
  // The big last bubble pops with a sparkle.
  const lastBorn = 300 + (count - 1) * 150
  spark(ctx, 'pop', 14 * ctx.k, '#9fd3ea', () => {
    const mouth = fishPoint(fishPose(lastBorn), ctx.unit, FISH_POINTS.mouth)
    return { x: mouth.x + (14 + 10 * noise(count - 1 + 5)) * ctx.k, y: mouth.y - (34 + 30 * noise(count - 1 + 9)) * ctx.k + 10 * ctx.k }
  }, lastBorn + 600, lastBorn + 790, 45)
  return duration
}

function salute(ctx: Ctx) {
  const duration = LOGO_SCENE_DURATIONS.salute
  const fishPose = (ms: number) => {
    const pop = popOut(ctx, ms, 0.5, 0, 300, duration)
    const bow = bump(ms, 420, 1250)
    return pose(ctx.home.x + pop.dx, ctx.home.y + pop.dy, { rotate: -4 * pop.p + 9 * bow, scaleX: pop.scale, scaleY: pop.scale })
  }
  fishSprites(ctx, fishPose, ms => seg(ms, 600, 650) - seg(ms, 1000, 1050), 'bare')
  const lift = (ms: number) => easeOut(seg(ms, 380, 620)) * (1 - easeIn(seg(ms, 1050, 1300)))
  const helmet = (ms: number): Pose => {
    const f = fishPose(ms), h = lift(ms)
    const at = fishPoint(f, ctx.unit, { x: FISH_POINTS.helmet.x + 1.4 * h, y: FISH_POINTS.helmet.y - 4.2 * h })
    return pose(at.x, at.y, { rotate: f.rotate - 28 * h, scaleX: f.scaleX, scaleY: f.scaleY })
  }
  ctx.sprites.push({ id: 'helmet', kind: { type: 'fish', part: 'helmet' }, width: HELMET_BOX.width * ctx.unit, height: HELMET_BOX.height * ctx.unit, pose: helmet })
  spark(ctx, 'glint', 11 * ctx.k, '#fff4c2', ms => { const h = helmet(ms); return { x: h.x + 4 * ctx.k, y: h.y - 5 * ctx.k } }, 640, 1000)
  return duration
}

function shake(ctx: Ctx) {
  const duration = LOGO_SCENE_DURATIONS.shake
  const fishPose = (ms: number) => {
    const pop = popOut(ctx, ms, 0.3, 0, 200, duration)
    const s = seg(ms, 200, 1000), decay = (1 - s) ** 0.6 * (s > 0 ? 1 : 0)
    const wobble = Math.sin(2 * Math.PI * 6 * s)
    return pose(ctx.home.x + pop.dx + 4 * ctx.k * Math.sin(2 * Math.PI * 6 * s + 1) * decay, ctx.home.y + pop.dy,
      { rotate: 14 * wobble * decay, scaleX: pop.scale * (1 + 0.06 * Math.abs(wobble) * decay), scaleY: pop.scale * (1 - 0.04 * Math.abs(wobble) * decay) })
  }
  // "Phew": eyes shut once it's done.
  fishSprites(ctx, fishPose, ms => seg(ms, 1020, 1060) - seg(ms, 1200, 1240))
  for (let n = 0; n < 7; n++) {
    const born = 300 + n * 40
    const local = { x: (noise(n) - 0.5) * 16, y: (noise(n + 7) - 0.5) * 8 }
    const dir = Math.atan2(local.y - 1.5, local.x || 0.1)
    ctx.sprites.push({ id: `drop${n}`, kind: { type: 'drop' }, width: 5 * ctx.k, height: 5 * ctx.k, pose: ms => {
      const t = (ms - born) / 1000
      if (t <= 0 || t > 0.55) return HIDDEN
      const o = fishPoint(fishPose(born), ctx.unit, local), v = (130 + 60 * noise(n + 2)) * ctx.k
      return pose(o.x + Math.cos(dir) * v * t, o.y + Math.sin(dir) * v * t + 380 * ctx.k * t * t, { opacity: 1 - seg(t, 0.3, 0.55) })
    } })
  }
  return duration
}

function war(ctx: Ctx) {
  const duration = LOGO_SCENE_DURATIONS.war
  const fishPose = (ms: number) => {
    const pop = popOut(ctx, ms, 0.45, 0, 250, duration)
    const chest = bump(ms, 260, 520)
    const shout = Math.sin(ms / 45) * bump(ms, 300, 1300)
    return pose(ctx.home.x + pop.dx + 1.5 * ctx.k * shout, ctx.home.y + pop.dy, { rotate: 6 * pop.p + 2 * shout, scaleX: pop.scale * (1 + 0.08 * chest), scaleY: pop.scale * (1 + 0.05 * chest) })
  }
  fishSprites(ctx, fishPose)
  const width = 74 * ctx.k, height = 38 * ctx.k
  ctx.sprites.push({ id: 'speech', kind: { type: 'text', text: 'WAR!', style: 'speech' }, width, height, pose: ms => {
    const s = seg(ms, 260, 470)
    if (s <= 0 || ms >= 1750) return HIDDEN
    const out = seg(ms, 1500, 1750)
    const grow = easeOutBack(s) * (1 - out)
    const f = fishPose(ms)
    const at = fishPoint(f, ctx.unit, { x: FISH_POINTS.mouth.x + 2, y: FISH_POINTS.mouth.y - 3 })
    return pose(at.x + width * 0.55, at.y - height * 0.15, { scaleX: grow, scaleY: grow, rotate: -4 + 4 * Math.sin(ms / 70) * (1 - seg(ms, 470, 900)), opacity: 1 - out })
  } })
  return duration
}

function chase(ctx: Ctx) {
  const duration = LOGO_SCENE_DURATIONS.chase
  const r = ctx.layout.fish.width * 0.42
  const centre = { x: ctx.home.x, y: ctx.home.y + r }
  const spin = (ms: number) => easeInOut(seg(ms, 250, 1450))
  const fishPose = (ms: number) => {
    const pop = popOut(ctx, ms, 0.3, 0, 250, duration)
    const phi = spin(ms) * 2 * 2 * Math.PI
    const dizzy = ms > 1450 ? 8 * Math.sin((ms - 1450) / 55) * (1 - seg(ms, 1450, 1850)) : 0
    const orbit = { x: centre.x + r * Math.sin(phi), y: centre.y - r * Math.cos(phi) }
    return pose(orbit.x + pop.dx, orbit.y + pop.dy, { rotate: deg(phi) + dizzy, scaleX: pop.scale, scaleY: pop.scale })
  }
  fishSprites(ctx, fishPose, ms => seg(ms, 1450, 1500) - seg(ms, 1780, 1830))
  for (let n = 0; n < 3; n++) {
    spark(ctx, `dizzy${n}`, 10 * ctx.k, '#f2c14e', ms => {
      const f = fishPose(ms), head = fishPoint(f, ctx.unit, { x: FISH_POINTS.helmet.x, y: FISH_POINTS.helmet.y - 3 })
      const a = ms / 180 + n * 2 * Math.PI / 3
      return { x: head.x + 13 * ctx.k * Math.cos(a), y: head.y + 4 * ctx.k * Math.sin(a) }
    }, 1450, 1880, 180)
  }
  return duration
}

/**
 * Ricochet combo: two or three enemies line up across the band, and one round
 * bounces from each to the next. They go down in turn, each scoring more, and
 * a big "x3 COMBO!" chip bursts over them.
 */
function fireCombo(ctx: Ctx, enemies: readonly LogoEnemy[]) {
  const duration = LOGO_SCENE_DURATIONS['fire-combo']
  const n = enemies.length
  const sizes = enemies.map(enemy => enemySize(ctx, enemy))
  // Left to right across the band, zig-zagging high and low, so the bounce reads.
  const flip = ctx.rng() < 0.5 ? 0 : 1
  const targets = enemies.map((_, i) => bandPoint(ctx, (i + 0.2 + 0.6 * ctx.rng()) / n, (i + flip) % 2 ? 0.6 + 0.4 * ctx.rng() : 0.4 * ctx.rng(), sizes[i]!.width / 2))
  const enter = enemies.map((_, i) => i * 220)
  const paths = enemies.map((enemy, i) => later(enemySwim(ctx, enemy, targets[i]!, 900, sizes[i]!.width), enter[i]!))
  const shotAt = 1500
  const hits: number[] = [shotAt + FLIGHT]
  for (let i = 1; i < n; i++) {
    const from = targets[i - 1]!, to = targets[i]!
    hits.push(hits[i - 1]! + 40 + clamp(Math.hypot(to.x - from.x, to.y - from.y) / (1.4 * ctx.k), 90, 200))
  }
  const last = hits[n - 1]!
  const spot = firingSpot(ctx, targets)
  const fishPose = gunnerPose(ctx, duration, spot, [{ at: shotAt, aim: aimAt(ctx, spot, paths[0]!(hits[0]!)) }], { raise: 700, lower: 2700, proud: last + 250 })
  fishSprites(ctx, fishPose, ms => seg(ms, 1000, 1060) - seg(ms, last + 200, last + 260))
  enemies.forEach((enemy, i) => {
    addEnemy(ctx, `enemy${i}`, enemy, paths[i]!, hits[i]!, enter[i]!)
    if (enemy === 'angler') addLure(ctx, `lure${i}`, paths[i]!, enter[i]!, hits[i]!)
  })
  // One round: muzzle to the first, then a ricochet to each of the others.
  addShot(ctx, { id: 'combo0', enemy: enemies[0]!, at: shotAt, target: paths[0]!, fishPose, text: '+100' })
  for (let i = 1; i < n; i++) {
    const from = paths[i - 1]!(hits[i - 1]!), to = paths[i]!(hits[i]!)
    const leave = hits[i - 1]! + 40
    tracers(ctx, `combo${i}`, from, to, leave, hits[i]! - leave)
    spark(ctx, `combo${i}-ricochet`, 14 * ctx.k, '#fff6d6', () => from, hits[i - 1]! + 10, hits[i - 1]! + 200, 120)
    explode(ctx, `combo${i}`, enemies[i]!, to, hits[i]!, `+${100 * 2 ** i}`)
  }
  const width = (9 * 13 + 26) * ctx.k, height = 30 * ctx.k
  const mid = { x: targets.reduce((sum, t) => sum + t.x, 0) / n, y: Math.min(...targets.map(t => t.y)) - 30 * ctx.k }
  const at = { x: clamp(mid.x, width / 2 + 8, ctx.W - width / 2 - 8), y: Math.max(height, mid.y) }
  ctx.sprites.push({ id: 'combo', kind: { type: 'text', text: `x${n} COMBO!`, style: 'combo' }, width, height, pose: ms => {
    const s = seg(ms, last + 120, last + 1100)
    if (s <= 0 || s >= 1) return HIDDEN
    const grow = easeOutBack(seg(s, 0, 0.25)) * (1 - easeIn(seg(s, 0.85, 1)))
    return pose(at.x, at.y - 10 * ctx.k * easeOut(s), { scaleX: grow, scaleY: grow, rotate: -6 + 5 * Math.sin(ms / 60) * (1 - seg(s, 0, 0.4)), opacity: 1 - seg(s, 0.85, 1) })
  } })
  for (let i = 0; i < 6; i++) {
    const a = i * Math.PI / 3 + 0.3
    spark(ctx, `combo-star${i}`, (9 + 4 * (i % 2)) * ctx.k, PARTY[i % PARTY.length]!, ms => {
      const r = (width * 0.45 + 18 * ctx.k * easeOut(seg(ms, last + 140, last + 640)))
      return { x: at.x + Math.cos(a) * r, y: at.y + Math.sin(a) * r * 0.45 }
    }, last + 140, last + 640, 180)
  }
  return duration
}

// ---- the ridiculous ones -----------------------------------------------------

/** Steps through 0..1 with a pause on each of `steps` beats (monotonic): a stutter-glide. */
const stepped = (s: number, steps: number) => s - Math.sin(2 * Math.PI * steps * s) / (2 * Math.PI * steps)

/** A speech bubble sized to its text, `height` tall, following `at` between `from` and `to` ms. */
function speech(ctx: Ctx, id: string, text: string, height: number, at: (ms: number) => { x: number; y: number }, from: number, to: number) {
  const width = height * (0.42 * text.length + 0.7)
  ctx.sprites.push({ id, kind: { type: 'text', text, style: 'speech' }, width, height, pose: ms => {
    if (ms <= from || ms >= to) return HIDDEN
    const grow = easeOutBack(seg(ms, from, from + 200)) * (1 - easeIn(seg(ms, to - 180, to)))
    const p = at(ms)
    return pose(clamp(p.x, width / 2 + 4, ctx.W - width / 2 - 4), clamp(p.y, height / 2 + 4, ctx.H - height / 2 - 4), { scaleX: grow, scaleY: grow, rotate: -3 + 3 * Math.sin(ms / 80) })
  } })
  return width
}

/** A little puff of smoke for magic and dust, `n` around a centre. */
function puffs(ctx: Ctx, id: string, count: number, at: () => { x: number; y: number }, from: number, radius: number, size = 10, colour?: string) {
  for (let n = 0; n < count; n++) {
    const a = n * 2 * Math.PI / count + 0.5
    ctx.sprites.push({ id: `${id}${n}`, kind: colour ? { type: 'smoke', colour } : { type: 'smoke' }, width: size * ctx.k, height: size * ctx.k, pose: ms => {
      const s = seg(ms, from, from + 420)
      if (s <= 0 || s >= 1) return HIDDEN
      const c = at(), grow = 0.6 + 1.2 * easeOut(s)
      return pose(c.x + Math.cos(a) * radius * easeOut(s), c.y + Math.sin(a) * radius * 0.7 * easeOut(s) - 6 * ctx.k * s, { opacity: 0.75 * (1 - s), scaleX: grow, scaleY: grow })
    } })
  }
}

/** Disco moonwalk: down onto the dance floor, moonwalks across, pirouettes under a mirror ball, strikes the pose, moonwalks home. */
function dance(ctx: Ctx) {
  const duration = LOGO_SCENE_DURATIONS.dance
  const { fish } = ctx.layout
  const big = 1.3
  const floorY = Math.min(ctx.H - fish.height * big, ctx.home.y + fish.height * 2.5)
  const farX = Math.max(ctx.home.x + fish.width, Math.min(ctx.W - fish.width * big / 2 - 16, ctx.home.x + clamp((ctx.W - ctx.home.x) * 0.6, 130, 520)))
  const out = (ms: number) => easeInOut(seg(ms, 0, 380)) * (1 - easeInOut(seg(ms, 3000, 3380)))
  // Gliding right (420-1500) facing left, then back (2250-3000) facing right: a moonwalk both ways.
  const glide = (ms: number) => stepped(seg(ms, 420, 1500), 5) * (1 - stepped(seg(ms, 2250, 3000), 5))
  const moving = (ms: number) => bump(ms, 420, 1500) + bump(ms, 2250, 3000)
  const face = (ms: number) => ms < 1500 ? lerp(1, -1, easeInOut(seg(ms, 280, 420))) : -Math.cos(Math.PI * 3 * easeInOut(seg(ms, 1520, 1900)))
  const fishPose = (ms: number): Pose => {
    const o = out(ms), scale = lerp(1, big, o), m = moving(ms)
    const x = lerp(ctx.home.x, farX, glide(ms))
    const bob = 4 * ctx.k * Math.abs(Math.sin(Math.PI * ms / 130)) * m
    const strut = bump(ms, 1900, 2250)
    const f = face(ms)
    return pose(lerp(ctx.home.x, x, o), lerp(ctx.home.y, floorY, o) - bob - 8 * ctx.k * strut, {
      rotate: -7 * Math.sign(f) * m - 18 * strut + 4 * Math.sin(ms / 60) * strut,
      scaleX: scale * (Math.abs(f) < 0.15 ? Math.sign(f || 1) * 0.15 : f), scaleY: scale * (1 + 0.05 * Math.sin(Math.PI * ms / 130) * m),
    })
  }
  // The mirror ball drops in over the far end of the floor.
  const ballSize = 26 * ctx.k
  const ball = { x: farX, y: Math.max(ballSize, floorY - fish.height * big * 1.15) }
  ctx.sprites.push({ id: 'disco', kind: { type: 'disco' }, width: ballSize, height: ballSize, pose: ms => {
    if (ms <= 1300 || ms >= 2500) return HIDDEN
    const grow = easeOutBack(seg(ms, 1300, 1550)) * (1 - easeIn(seg(ms, 2300, 2500)))
    return pose(ball.x, ball.y + 3 * ctx.k * Math.sin(ms / 140), { scaleX: grow * (0.92 + 0.08 * Math.cos(ms / 50)), scaleY: grow, rotate: 8 * Math.sin(ms / 200) })
  } })
  fishSprites(ctx, fishPose, ms => seg(ms, 1950, 2000) - seg(ms, 2200, 2250))
  for (let n = 0; n < 4; n++) {
    spark(ctx, `glint${n}`, (7 + 3 * (n % 2)) * ctx.k, PARTY[n]!, ms => {
      const a = ms / 220 + n * Math.PI / 2
      return { x: ball.x + Math.cos(a) * ballSize * 1.1, y: ball.y + Math.sin(a) * ballSize * 0.6 }
    }, 1500 + n * 60, 2300, 200)
    // Floor lights flash under its fins.
    spark(ctx, `floor${n}`, 9 * ctx.k, PARTY[(n + 2) % 4]!, () => ({ x: farX + (n - 1.5) * fish.width * 0.55, y: floorY + fish.height * big * 0.55 }), 1550 + n * 110, 1950 + n * 110, 45)
  }
  // Music notes bubble off its head the whole time.
  for (let n = 0; n < 9; n++) {
    const born = 450 + n * 230
    const size = (14 + 4 * noise(n)) * ctx.k
    ctx.sprites.push({ id: `note${n}`, kind: { type: 'note', colour: PARTY[n % 4]! }, width: size, height: size, pose: ms => {
      const s = seg(ms, born, born + 900)
      if (s <= 0 || s >= 1) return HIDDEN
      const head = fishPoint(fishPose(born), ctx.unit, { x: FISH_POINTS.helmet.x, y: FISH_POINTS.helmet.y - 3 })
      return pose(head.x + 9 * ctx.k * Math.sin(s * 6 + n), Math.max(size / 2, head.y - 40 * ctx.k * easeOut(s)), { opacity: seg(s, 0, 0.1) * (1 - seg(s, 0.7, 1)), rotate: 15 * Math.sin(s * 8 + n), scaleX: 0.6 + 0.4 * easeOut(s), scaleY: 0.6 + 0.4 * easeOut(s) })
    } })
  }
  return duration
}

/** Hula hoop: a hoop drops over it, it swings its hips to keep it spinning, then lets it drop and takes a bow. */
function hula(ctx: Ctx) {
  const duration = LOGO_SCENE_DURATIONS.hula
  const { fish } = ctx.layout
  const drop = fish.height * 1.6
  const spinning = (ms: number) => seg(ms, 480, 640) * (1 - seg(ms, 1850, 2050))
  const angle = (ms: number) => 2 * Math.PI * ms / 280
  const fishPose = (ms: number): Pose => {
    const pop = popOut(ctx, ms, 0.45, 0, 300, duration)
    const s = spinning(ms), a = angle(ms), sway = Math.sin(a) * s
    const bow = bump(ms, 2000, 2350)
    return pose(ctx.home.x + pop.dx + 5 * ctx.k * sway, ctx.home.y + pop.dy + drop * pop.p + 1.5 * ctx.k * Math.cos(2 * a) * s,
      { rotate: -6 * sway + 10 * bow, scaleX: pop.scale, scaleY: pop.scale * (1 + 0.03 * Math.cos(2 * a) * s) })
  }
  const hoopWidth = fish.width * 1.45 * 1.15, hoopHeight = hoopWidth * 0.3
  const hoop = (ms: number) => {
    const f = fishPose(ms), s = spinning(ms), a = angle(ms)
    const waist = fishPoint(f, ctx.unit, { x: -1.5, y: 2.2 })
    const fall = (1 - easeIn(seg(ms, 280, 520))) * fish.height * 0.8
    const slide = easeIn(seg(ms, 1880, 2250)) * fish.height * 1.4
    return { x: Math.max(hoopWidth / 2 + 2, waist.x - 6 * ctx.k * Math.sin(a) * s), y: waist.y - fall + slide, tilt: -6 + 9 * Math.sin(a + 1) * s, visible: ms > 280 && ms < 2250, fade: seg(ms, 280, 340) * (1 - seg(ms, 2120, 2250)), a }
  }
  const hoopSprite = (half: 'back' | 'front') => ctx.sprites.push({ id: `hoop-${half}`, kind: { type: 'hoop', half }, width: hoopWidth, height: hoopHeight, pose: ms => {
    const h = hoop(ms)
    return h.visible ? pose(h.x, h.y, { rotate: h.tilt, opacity: h.fade }) : HIDDEN
  } })
  // A glint racing round the hoop sells the spin: drawn behind the carp on the far side, in front on the near side.
  const glint = (half: 'back' | 'front', n: number) => ctx.sprites.push({ id: `hoop-glint-${half}${n}`, kind: { type: 'spark', colour: '#fff3a8' }, width: 7 * ctx.k, height: 7 * ctx.k, pose: ms => {
    const h = hoop(ms)
    const t = h.a * 1.5 + n * Math.PI
    const front = Math.sin(t) >= 0
    if (!h.visible || front !== (half === 'front') || spinning(ms) < 0.05) return HIDDEN
    const dx = Math.cos(t) * hoopWidth * 0.46, dy = Math.sin(t) * hoopHeight * 0.36, r = h.tilt * Math.PI / 180
    return pose(h.x + dx * Math.cos(r) - dy * Math.sin(r), h.y + dx * Math.sin(r) + dy * Math.cos(r), { opacity: h.fade * spinning(ms), rotate: deg(t) })
  } })
  hoopSprite('back'); glint('back', 0); glint('back', 1)
  fishSprites(ctx, fishPose, ms => seg(ms, 2050, 2100) - seg(ms, 2280, 2330))
  hoopSprite('front'); glint('front', 0); glint('front', 1)
  for (const side of [-1, 1]) {
    spark(ctx, `whoosh${side}`, 9 * ctx.k, '#e8508a', ms => { const h = hoop(ms); return { x: clamp(h.x + side * hoopWidth * 0.62, 10 * ctx.k, ctx.W - 10 * ctx.k), y: h.y - 4 * ctx.k } }, 900 + (side + 1) * 250, 1300 + (side + 1) * 250, 120)
  }
  return duration
}

/** Juggling: drops below the header and keeps three tiny fish in the air off its nose, then they swim off. */
function juggle(ctx: Ctx) {
  const duration = LOGO_SCENE_DURATIONS.juggle
  const { fish } = ctx.layout
  const drop = fish.height * 2.3
  const period = 470, start = 450, rounds = 3
  const act = (ms: number) => seg(ms, 350, 480) * (1 - seg(ms, 2150, 2350))
  const fishPose = (ms: number): Pose => {
    const pop = popOut(ctx, ms, 0.4, 0, 320, duration)
    const a = act(ms)
    const nod = Math.max(0, Math.cos(2 * Math.PI * (ms - start) / (period / 3))) ** 6 * a
    const proud = bump(ms, 2250, 2550)
    return pose(ctx.home.x + pop.dx, ctx.home.y + pop.dy + drop * pop.p - 3 * ctx.k * nod - 6 * ctx.k * proud, { rotate: -14 * a + 5 * nod, scaleX: pop.scale, scaleY: pop.scale * (1 + 0.05 * proud) })
  }
  fishSprites(ctx, fishPose, ms => seg(ms, 2250, 2290) - seg(ms, 2450, 2490))
  const colours = ['#f08a3c', '#4fb3d9', '#e8508a']
  const height = fish.height * 1.25
  colours.forEach((colour, i) => {
    const launch = start + i * period / 3, land = launch + rounds * period
    const size = 16 * ctx.k
    ctx.sprites.push({ id: `minnow${i}`, kind: { type: 'minnow', colour }, width: size * 1.5, height: size, pose: ms => {
      if (ms <= launch || ms >= land + 320) return HIDDEN
      const nose = fishPoint(fishPose(ms), ctx.unit, { x: FISH_POINTS.mouth.x + 0.5, y: FISH_POINTS.mouth.y - 1.5 })
      if (ms >= land) {
        // Off it swims, with a flick of the tail.
        const s = seg(ms, land, land + 320)
        return pose(nose.x + (30 + 10 * i) * ctx.k * easeOut(s), nose.y - (24 - 10 * i) * ctx.k * Math.sin(Math.PI * s * 0.8), { opacity: 1 - easeIn(s), rotate: -20 + 30 * s })
      }
      const u = ((ms - launch) / period) % 1
      return pose(nose.x - 18 * ctx.k * Math.sin(Math.PI * u), Math.max(size, nose.y - height * 4 * u * (1 - u)), { rotate: -360 * u, opacity: seg(ms, launch, launch + 60) })
    } })
    spark(ctx, `catch${i}`, 8 * ctx.k, '#fff3a8', ms => fishPoint(fishPose(ms), ctx.unit, { x: FISH_POINTS.mouth.x, y: FISH_POINTS.mouth.y - 3 }), launch - 20, launch + 160, 90)
  })
  return duration
}

/** Magic trick: a top hat, three taps of the wand, poof, a rabbit-fish pops out, TA-DA!, poof, gone, bow. */
function magic(ctx: Ctx) {
  const duration = LOGO_SCENE_DURATIONS.magic
  const { fish } = ctx.layout
  const drop = fish.height * 1.8
  const taps = [650, 850, 1050]
  const fishPose = (ms: number): Pose => {
    const pop = popOut(ctx, ms, 0.45, 0, 300, duration)
    const bow = bump(ms, 2250, 2650)
    return pose(ctx.home.x + pop.dx, ctx.home.y + pop.dy + drop * pop.p, { rotate: -4 * pop.p + 14 * bow, scaleX: pop.scale, scaleY: pop.scale })
  }
  const rest = fishPose(1000)
  const hatW = 36 * ctx.k, hatH = 31 * ctx.k
  const hatAt = { x: Math.min(ctx.W - hatW, rest.x + fish.width * 1.45 * 0.5 + hatW * 1.1), y: rest.y + fish.height * 0.2 }
  const tap = (ms: number) => taps.reduce((sum, at) => sum + bump(ms, at - 80, at + 80), 0)
  const shown = (ms: number) => easeOutBack(seg(ms, 300, 450)) * (1 - easeIn(seg(ms, 2150, 2250)))
  // The rabbit-fish springs out of the hat and wiggles its ears.
  const bunnyW = 32 * ctx.k, bunnyH = 32 * ctx.k
  const bunny = (ms: number) => ({ x: hatAt.x, y: hatAt.y - hatH * 0.45 - bunnyH * 0.55 * easeOutBack(seg(ms, 1280, 1500)) })
  ctx.sprites.push({ id: 'rabbit', kind: { type: 'minnow', colour: '#f4f1e8', ears: true }, width: bunnyW, height: bunnyH, pose: ms => {
    if (ms <= 1280 || ms >= 2250) return HIDDEN
    const b = bunny(ms), wiggle = Math.sin(ms / 45) * bump(ms, 1550, 1900), hop = Math.abs(Math.sin((ms - 1500) / 110)) * seg(ms, 1500, 1600) * (1 - seg(ms, 1950, 2050))
    return pose(b.x, b.y - 4 * ctx.k * hop, { scaleX: shown(ms), scaleY: shown(ms) * (1 + 0.08 * wiggle), rotate: 6 * wiggle, opacity: seg(ms, 1280, 1320) })
  } })
  // The hat goes on top, so the rabbit climbs out of it.
  ctx.sprites.push({ id: 'hat', kind: { type: 'hat' }, width: hatW, height: hatH, pose: ms => {
    if (ms <= 300 || ms >= 2250) return HIDDEN
    const g = shown(ms), jolt = tap(ms) + bump(ms, 1250, 1400)
    return pose(hatAt.x, hatAt.y + 2 * ctx.k * jolt, { scaleX: g * (1 + 0.06 * jolt), scaleY: g * (1 - 0.08 * jolt), rotate: 4 * Math.sin(ms / 90) * bump(ms, 1250, 1500) })
  } })
  fishSprites(ctx, fishPose, ms => seg(ms, 1200, 1250) - seg(ms, 1400, 1450))
  const wandLength = 24 * ctx.k
  ctx.sprites.push({ id: 'wand', kind: { type: 'wand' }, width: wandLength, height: 4 * ctx.k, pose: ms => {
    if (ms <= 380 || ms >= 2250) return HIDDEN
    const f = fishPose(ms)
    const grip = fishPoint(f, ctx.unit, FISH_POINTS.mouth)
    const a = (-38 + 40 * tap(ms) + 25 * bump(ms, 1180, 1320)) * Math.PI / 180
    return pose(grip.x + Math.cos(a) * wandLength * 0.45, grip.y + Math.sin(a) * wandLength * 0.45, { rotate: deg(a), opacity: seg(ms, 380, 430) * (1 - seg(ms, 2150, 2250)) })
  } })
  for (const [n, at] of taps.entries()) spark(ctx, `tap${n}`, 9 * ctx.k, '#fff3a8', () => ({ x: hatAt.x + (n - 1) * 6 * ctx.k, y: hatAt.y - hatH * 0.5 }), at - 20, at + 200, 90)
  puffs(ctx, 'poof-in', 4, () => hatAt, 280, 16 * ctx.k)
  puffs(ctx, 'poof', 6, () => ({ x: hatAt.x, y: hatAt.y - hatH * 0.4 }), 1230, 22 * ctx.k, 12)
  puffs(ctx, 'poof-out', 7, () => ({ x: hatAt.x, y: hatAt.y - hatH * 0.3 }), 2100, 24 * ctx.k, 13)
  for (let n = 0; n < 4; n++) spark(ctx, `sparkle${n}`, 11 * ctx.k, PARTY[n]!, () => ({ x: hatAt.x + Math.cos(n * 1.7) * hatW, y: hatAt.y - hatH * 0.8 + Math.sin(n * 1.7) * hatH * 0.4 }), 1260 + n * 50, 1560 + n * 50, 160)
  const tadaH = 30 * ctx.k
  speech(ctx, 'tada', 'TA-DA!', tadaH, () => ({ x: hatAt.x + hatW * 0.6 + tadaH * 1.8, y: hatAt.y - hatH * 1.2 }), 1450, 2150)
  return duration
}

/** Skateboard: hops onto a board, rides across with an ollie kickflip, power-slides round and rides home. */
function skate(ctx: Ctx) {
  const duration = LOGO_SCENE_DURATIONS.skate
  const { fish } = ctx.layout
  const big = 1.2
  const floorY = Math.min(ctx.H - fish.height * 1.6, ctx.home.y + fish.height * 2.3)
  const farX = Math.max(ctx.home.x + fish.width, Math.min(ctx.W - fish.width * big / 2 - 20, ctx.home.x + clamp((ctx.W - ctx.home.x) * 0.65, 140, 560)))
  const ride = (ms: number) => easeInOut(seg(ms, 420, 1500)) * (1 - easeInOut(seg(ms, 1800, 2700)))
  const speed = (ms: number) => (ride(ms + 8) - ride(ms - 8)) / 16 * (1080 / 1.6)
  const midOllie = 820
  const ollie = (ms: number) => Math.sin(Math.PI * seg(ms, midOllie, midOllie + 360))
  // Turns round in the power slide for the ride home, and back again to hop into the logo.
  const homeFace = (ms: number) => ms > 2700 ? lerp(-1, 1, easeInOut(seg(ms, 2720, 2900))) : lerp(1, -1, easeInOut(seg(ms, 1520, 1760)))
  const onBoard = (ms: number) => easeInOut(seg(ms, 0, 360)) * (1 - easeInOut(seg(ms, 2760, 3180)))
  const boardY = floorY + fish.height * big * 0.55
  const fishPose = (ms: number): Pose => {
    const o = onBoard(ms), scale = lerp(1, big, o)
    const x = lerp(ctx.home.x, farX, ride(ms))
    const air = 30 * ctx.k * ollie(ms)
    const slide = bump(ms, 1500, 1800)
    const f = homeFace(ms)
    const hopHome = bump(ms, 2760, 3180)
    return pose(lerp(ctx.home.x, x, o), lerp(ctx.home.y, floorY, o) - air - 14 * ctx.k * hopHome, {
      rotate: clamp(6 * speed(ms), -10, 10) - 10 * ollie(ms) * Math.sign(f) + 8 * slide * Math.sin(ms / 40),
      scaleX: scale * (Math.abs(f) < 0.15 ? Math.sign(f || 1) * 0.15 : f), scaleY: scale,
    })
  }
  const boardW = fish.width * 1.15, boardH = boardW * 0.25
  const board = (ms: number) => {
    const f = fishPose(ms), o = onBoard(ms)
    const kickOff = seg(ms, 2760, 3050)
    return { x: f.x + 18 * ctx.k * kickOff, y: (o > 0.98 || ms < 2700 ? boardY - 30 * ctx.k * ollie(ms) : boardY) - 20 * ctx.k * Math.sin(Math.PI * kickOff), kickOff }
  }
  ctx.sprites.push({ id: 'board', kind: { type: 'board' }, width: boardW, height: boardH, pose: ms => {
    if (ms <= 240 || ms >= 3050) return HIDDEN
    const b = board(ms), flip = Math.cos(2 * Math.PI * seg(ms, midOllie + 40, midOllie + 320))
    return pose(b.x, b.y, { scaleX: easeOutBack(seg(ms, 240, 380)), scaleY: flip, rotate: -14 * bump(ms, midOllie - 20, midOllie + 120) + 540 * easeIn(b.kickOff), opacity: 1 - seg(b.kickOff, 0.6, 1) })
  } })
  fishSprites(ctx, fishPose, ms => seg(ms, 1540, 1580) - seg(ms, 1760, 1800))
  // Speed lines behind it, and sparks off the wheels in the power slide.
  for (let n = 0; n < 3; n++) {
    ctx.sprites.push({ id: `speed${n}`, kind: { type: 'pixel', colour: '#9fd3ea' }, width: (16 + 6 * n) * ctx.k, height: 2 * ctx.k, pose: ms => {
      const v = speed(ms)
      if (Math.abs(v) < 0.6 || ms < 450 || ms > 2680) return HIDDEN
      const f = fishPose(ms), width = (16 + 6 * n) * ctx.k
      const x = f.x - Math.sign(v) * (fish.width * 0.8 + 6 * n * ctx.k)
      if (x - width / 2 < 0 || x + width / 2 > ctx.W) return HIDDEN
      return pose(x, f.y - fish.height * 0.3 + n * 9 * ctx.k, { opacity: clamp((Math.abs(v) - 0.6) * 1.5, 0, 0.85) })
    } })
  }
  for (let n = 0; n < 4; n++) spark(ctx, `slide${n}`, 8 * ctx.k, n % 2 ? '#f2c14e' : '#fff3a8', ms => ({ x: board(ms).x + (n - 1.5) * 9 * ctx.k, y: boardY + boardH * 0.6 - 8 * ctx.k * seg(ms, 1500 + n * 60, 1760 + n * 60) }), 1500 + n * 60, 1760 + n * 60, 140)
  puffs(ctx, 'land', 3, () => ({ x: lerp(ctx.home.x, farX, ride(midOllie + 360)), y: boardY }), midOllie + 340, 14 * ctx.k, 8)
  return duration
}

/** Sneeze: swims out, "ah… ah…", ACHOO!, blown backwards head over tail, dizzy, home. */
function sneeze(ctx: Ctx) {
  const duration = LOGO_SCENE_DURATIONS.sneeze
  const { fish } = ctx.layout
  const big = 1.3
  const fwd = { x: Math.min(ctx.W - fish.width * 1.5 / 2 - 12, ctx.home.x + clamp((ctx.W - ctx.home.x) * 0.45, 100, 380)), y: Math.min(ctx.H - fish.height * 2, ctx.home.y + fish.height * 2.2) }
  const back = { x: Math.max(ctx.home.x + fish.width * 0.3, fish.width * big / 2 + 6), y: fwd.y }
  const achoo = 1150
  const fishPose = (ms: number): Pose => {
    const out = easeInOut(seg(ms, 0, 450)) * (1 - easeInOut(seg(ms, 1950, 2580)))
    const inhale = easeInOut(seg(ms, 500, achoo - 30)) * (1 - seg(ms, achoo - 30, achoo + 40))
    const blown = easeOut(seg(ms, achoo, achoo + 450))
    const twitch = Math.sin(ms / 22) * bump(ms, 520, 760) + Math.sin(ms / 18) * bump(ms, 850, 1080)
    const x = lerp(fwd.x, back.x, blown), y = fwd.y - 16 * ctx.k * Math.sin(Math.PI * blown)
    const dizzy = 7 * Math.sin((ms - 1600) / 55) * bump(ms, 1600, 1950)
    const scale = lerp(1, big, out) * (1 + 0.14 * inhale)
    return pose(lerp(ctx.home.x, x, out), lerp(ctx.home.y, y, out), {
      rotate: -14 * inhale - 360 * blown + 2 * twitch + dizzy,
      scaleX: scale * (1 + 0.04 * twitch), scaleY: scale * (1 - 0.06 * bump(ms, achoo, achoo + 120)),
    })
  }
  fishSprites(ctx, fishPose, ms => seg(ms, 950, 1000) - seg(ms, 1350, 1400) + seg(ms, 1600, 1640) - seg(ms, 1900, 1940))
  const mouth = (ms: number) => fishPoint(fishPose(ms), ctx.unit, FISH_POINTS.mouth)
  const ahH = 22 * ctx.k
  speech(ctx, 'ah', 'ah…', ahH, ms => { const m = mouth(ms); return { x: m.x + ahH * 1.6, y: m.y - ahH * 1.2 } }, 520, 800)
  speech(ctx, 'ahh', 'AH…', ahH * 1.2, ms => { const m = mouth(ms); return { x: m.x + ahH * 1.9, y: m.y - ahH * 1.3 } }, 830, achoo - 20)
  // ACHOO! stays where it happened while the carp goes flying.
  const at = mouth(achoo)
  const achooH = 36 * ctx.k
  speech(ctx, 'achoo', 'ACHOO!', achooH, () => ({ x: at.x + achooH * 1.9, y: at.y - achooH * 0.4 }), achoo, achoo + 800)
  for (let n = 0; n < 10; n++) {
    const a = (noise(n + 4) - 0.5) * 1.1
    ctx.sprites.push({ id: `spray${n}`, kind: { type: 'drop' }, width: 5 * ctx.k, height: 5 * ctx.k, pose: ms => {
      const t = (ms - achoo) / 1000
      if (t <= 0 || t > 0.5) return HIDDEN
      const v = (200 + 120 * noise(n)) * ctx.k
      return pose(at.x + Math.cos(a) * v * t, at.y + Math.sin(a) * v * t + 300 * ctx.k * t * t, { opacity: 1 - seg(t, 0.25, 0.5) })
    } })
  }
  puffs(ctx, 'blast', 3, () => at, achoo, 12 * ctx.k, 9)
  for (let n = 0; n < 3; n++) {
    spark(ctx, `dizzy${n}`, 9 * ctx.k, '#f2c14e', ms => {
      const head = fishPoint(fishPose(ms), ctx.unit, { x: FISH_POINTS.helmet.x, y: FISH_POINTS.helmet.y - 3 })
      const a = ms / 170 + n * 2 * Math.PI / 3
      return { x: head.x + 13 * ctx.k * Math.cos(a), y: head.y + 4 * ctx.k * Math.sin(a) }
    }, 1580, 1980, 180)
  }
  return duration
}

// ---- the newer crowd: other carp, a rocket, a balloon, a disguise, a barbell --

/** Another carp (CarpLook), logo-sized, facing right at scaleX 1. */
function carpSprite(ctx: Ctx, id: string, look: CarpLook, at: (ms: number) => Pose) {
  ctx.sprites.push({ id, kind: { type: 'carp', look }, width: ctx.layout.fish.width, height: ctx.layout.fish.height, pose: at })
}

/** A `length`-long prop held in the mouth of a carp posed at `p`, angled `a` degrees up from its nose (mirrors when it faces left). */
function heldAt(ctx: Ctx, p: Pose, a: number, length: number) {
  const face = Math.sign(p.scaleX) || 1
  const grip = fishPoint(p, ctx.unit, FISH_POINTS.mouth)
  const angle = p.rotate + (face > 0 ? a : 180 - a), r = angle * Math.PI / 180
  return pose(grip.x + Math.cos(r) * length * 0.45, grip.y + Math.sin(r) * length * 0.45, { rotate: angle, opacity: p.opacity })
}

/** Facing from one spot to another, never thinner than a sliver (so it turns rather than vanishing). */
function facing(from: { x: number }, to: { x: number }, soft: number) {
  const f = Math.tanh((to.x - from.x) / soft)
  return Math.abs(f) < 0.15 ? Math.sign(f || 1) * 0.15 : f
}

/** The gags' stage floor: just below the header, `dy` carp-heights under the logo. */
const floorY = (ctx: Ctx, dy: number) => Math.min(ctx.H - ctx.layout.fish.height * 2, ctx.home.y + ctx.layout.fish.height * dy)

/** Dizzy stars circling a carp's head. */
function dizzy(ctx: Ctx, id: string, fishPose: (ms: number) => Pose, from: number, to: number) {
  for (let n = 0; n < 3; n++) {
    spark(ctx, `${id}${n}`, 9 * ctx.k, '#f2c14e', ms => {
      const head = fishPoint(fishPose(ms), ctx.unit, { x: FISH_POINTS.helmet.x, y: FISH_POINTS.helmet.y - 3 })
      const a = ms / 170 + n * 2 * Math.PI / 3
      return { x: head.x + 13 * ctx.k * Math.cos(a), y: head.y + 4 * ctx.k * Math.sin(a) }
    }, from, to, 180)
  }
}

/** Sword fight: a pirate rival carp swims up, three parries, the carp disarms it, both bow, the rival flees. */
function duel(ctx: Ctx) {
  const duration = LOGO_SCENE_DURATIONS.duel
  const { fish } = ctx.layout
  const big = 1.25, half = fish.width * big / 2
  const y = floorY(ctx, 2.3)
  const gap = fish.width * big * 1.55
  const heroX = clamp(ctx.home.x + fish.width * 0.5, half + 6, ctx.W - gap - half - 6)
  const rivalX = heroX + gap
  const clashes = [1100, 1350, 1600], disarm = 1850
  const lunge = (ms: number) => clashes.reduce((sum, at) => sum + bump(ms, at - 120, at + 120), 0)
  const out = (ms: number) => easeInOut(seg(ms, 0, 380)) * (1 - easeInOut(seg(ms, 2450, 2950)))
  const heroPose = (ms: number): Pose => {
    const o = out(ms), scale = lerp(1, big, o)
    const thrust = 0.16 * fish.width * lunge(ms) + 0.3 * fish.width * bump(ms, disarm - 150, disarm + 200)
    const bounce = 3 * ctx.k * Math.abs(Math.sin(ms / 90)) * seg(ms, 450, 600) * (1 - seg(ms, 1950, 2050))
    return pose(lerp(ctx.home.x, heroX + thrust, o), lerp(ctx.home.y, y, o) - bounce, { rotate: 14 * bump(ms, 2050, 2400) - 4 * lunge(ms), scaleX: scale, scaleY: scale })
  }
  const rivalPose = (ms: number): Pose => {
    if (ms <= 250 || ms >= 2750) return HIDDEN
    const flee = easeIn(seg(ms, 2400, 2750))
    const start = Math.min(ctx.W - half - 4, rivalX + fish.width * 0.5)
    const x = lerp(start, rivalX, easeOut(seg(ms, 250, 600))) - 0.16 * fish.width * lunge(ms) + 0.25 * fish.width * bump(ms, disarm, disarm + 300)
    const shock = 10 * Math.sin(ms / 30) * bump(ms, disarm, disarm + 350)
    return pose(lerp(x, Math.min(ctx.W - half - 2, rivalX + fish.width), flee), y, {
      rotate: -14 * bump(ms, 2050, 2400) + 4 * lunge(ms) + shock, scaleX: -big, scaleY: big, opacity: seg(ms, 250, 400) * (1 - flee),
    })
  }
  carpSprite(ctx, 'rival', 'rival', rivalPose)
  fishSprites(ctx, heroPose, ms => seg(ms, 2100, 2150) - seg(ms, 2350, 2400))
  const length = fish.width * 0.8
  const blade = (ms: number) => -28 + 22 * lunge(ms) + 30 * bump(ms, disarm - 150, disarm + 200)
  const tip = (p: Pose) => { const r = p.rotate * Math.PI / 180; return { x: p.x + Math.cos(r) * length * 0.5, y: p.y + Math.sin(r) * length * 0.5 } }
  ctx.sprites.push({ id: 'sword', kind: { type: 'sword' }, width: length, height: length * 0.2, pose: ms => {
    if (ms <= 450 || ms >= 2450) return HIDDEN
    const p = heldAt(ctx, heroPose(ms), blade(ms), length)
    return { ...p, opacity: seg(ms, 450, 550) * (1 - seg(ms, 2300, 2450)) }
  } })
  const flung = heldAt(ctx, rivalPose(disarm), blade(disarm), length)
  ctx.sprites.push({ id: 'rival-sword', kind: { type: 'sword' }, width: length, height: length * 0.2, pose: ms => {
    if (ms <= 450 || ms >= disarm + 600) return HIDDEN
    if (ms < disarm) return { ...heldAt(ctx, rivalPose(ms), blade(ms), length), opacity: seg(ms, 450, 550) }
    // Knocked spinning out of its fin, up and away.
    const s = seg(ms, disarm, disarm + 600)
    return pose(Math.min(ctx.W - length / 2, flung.x + 50 * ctx.k * s), Math.max(length / 2, flung.y - 70 * ctx.k * Math.sin(Math.PI * s * 0.8)), { rotate: flung.rotate + 540 * s, opacity: 1 - seg(s, 0.6, 1) })
  } })
  clashes.forEach((at, n) => spark(ctx, `clash${n}`, 13 * ctx.k, '#fff3a8', () => {
    const a = tip(heldAt(ctx, heroPose(at), blade(at), length)), b = tip(heldAt(ctx, rivalPose(at), blade(at), length))
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
  }, at - 30, at + 200, 120))
  const h = 26 * ctx.k
  speech(ctx, 'en-garde', 'EN GARDE!', h, ms => { const p = heroPose(ms); return { x: p.x + h * 2, y: p.y - fish.height * 1.2 } }, 480, 1000)
  speech(ctx, 'parley', 'PARLEY!', h, ms => { const p = rivalPose(Math.min(ms, 2400)); return { x: p.x, y: p.y - fish.height * 1.2 } }, disarm + 80, 2400)
  return duration
}

/** The waltz: a lady carp appears, the carp offers her a rose, they twirl round each other under hearts, she curtsies and is gone. */
function waltz(ctx: Ctx) {
  const duration = LOGO_SCENE_DURATIONS.waltz
  const { fish } = ctx.layout
  const big = 1.2, half = fish.width * big / 2, r = fish.width * big * 0.62
  const c = { x: clamp(ctx.home.x + fish.width * 1.6, r + half + 6, ctx.W - r - half - 6), y: floorY(ctx, 2.4) }
  const spin = (ms: number) => 2 * Math.PI * easeInOut(seg(ms, 1250, 2450))
  const at = (a: number) => ({ x: c.x + r * Math.cos(a), y: c.y + r * 0.3 * Math.sin(a) })
  const heroSpot = (ms: number) => at(Math.PI + spin(ms)), ladySpot = (ms: number) => at(spin(ms))
  const out = (ms: number) => easeInOut(seg(ms, 0, 400)) * (1 - easeInOut(seg(ms, 2750, 3200)))
  const twirling = (ms: number) => seg(ms, 1250, 1400) * (1 - seg(ms, 2400, 2500))
  const heroPose = (ms: number): Pose => {
    const o = out(ms), scale = lerp(1, big, o), p = heroSpot(ms)
    const sway = Math.sin(ms / 120) * twirling(ms)
    return pose(lerp(ctx.home.x, p.x, o), lerp(ctx.home.y, p.y, o) - 4 * ctx.k * Math.abs(sway), {
      rotate: 12 * bump(ms, 600, 1150) + 5 * sway, scaleX: scale * lerp(1, facing(p, ladySpot(ms), r * 0.25), o), scaleY: scale,
    })
  }
  const ladyPose = (ms: number): Pose => {
    if (ms <= 400 || ms >= 2950) return HIDDEN
    const p = ladySpot(ms)
    // A twirl on the spot, then a curtsy.
    const f = Math.cos(2 * Math.PI * seg(ms, 2450, 2700)) * facing(p, heroSpot(ms), r * 0.25)
    const face = Math.abs(f) < 0.15 ? Math.sign(f || 1) * 0.15 : f
    const grow = 0.6 + 0.4 * easeOutBack(seg(ms, 400, 650))
    return pose(p.x, p.y + 3 * ctx.k * Math.sin(ms / 150), {
      rotate: -10 * bump(ms, 2700, 2950) * Math.sign(face), scaleX: big * grow * face, scaleY: big * grow, opacity: seg(ms, 400, 520) * (1 - seg(ms, 2800, 2950)),
    })
  }
  carpSprite(ctx, 'lady', 'lady', ladyPose)
  fishSprites(ctx, heroPose, ms => seg(ms, 2450, 2500) - seg(ms, 2700, 2750))
  const length = fish.width * 0.5
  ctx.sprites.push({ id: 'rose', kind: { type: 'flower' }, width: length, height: length * 0.45, pose: ms => {
    if (ms <= 550 || ms >= 2950) return HIDDEN
    // Held out in the carp's mouth, then handed over; she keeps it.
    const a = heldAt(ctx, heroPose(ms), -15, length), b = heldAt(ctx, ladyPose(ms), -15, length), t = easeInOut(seg(ms, 1100, 1250))
    const grow = easeOutBack(seg(ms, 550, 700))
    return pose(lerp(a.x, b.x, t), lerp(a.y, b.y, t), { rotate: lerp(a.rotate, b.rotate, t), scaleX: grow, scaleY: grow, opacity: t < 1 ? 1 : b.opacity })
  } })
  spark(ctx, 'appear', 14 * ctx.k, '#ff8fb0', () => ladySpot(400), 380, 700, 120)
  const colours = ['#e8508a', '#ff8fb0', '#d62f5f']
  for (let n = 0; n < 7; n++) {
    const born = 1200 + n * 190, size = (11 + 4 * noise(n + 2)) * ctx.k
    ctx.sprites.push({ id: `heart${n}`, kind: { type: 'heart', colour: colours[n % 3]! }, width: size, height: size * 0.92, pose: ms => {
      const s = seg(ms, born, born + 800)
      if (s <= 0 || s >= 1) return HIDDEN
      const a = heroSpot(born), b = ladySpot(born)
      return pose((a.x + b.x) / 2 + 10 * ctx.k * Math.sin(s * 6 + n), Math.max(size, (a.y + b.y) / 2 - fish.height * 0.4 - 46 * ctx.k * easeOut(s)),
        { opacity: seg(s, 0, 0.12) * (1 - seg(s, 0.7, 1)), scaleX: 0.6 + 0.4 * easeOut(s), scaleY: 0.6 + 0.4 * easeOut(s), rotate: 12 * Math.sin(s * 5 + n) })
    } })
  }
  return duration
}

/** Bottle rocket: one strapped on, it sputters, ignites, loops a wild figure eight, fizzles, and parachutes down. */
function rocket(ctx: Ctx) {
  const duration = LOGO_SCENE_DURATIONS.rocket
  const { fish } = ctx.layout
  const big = 1.2, m = fish.width * big * 0.7
  const ax = clamp(Math.min(ctx.W * 0.3, 240), 0, Math.max(0, (ctx.W - 2 * m) / 2))
  const ay = clamp(ctx.H * 0.14, 20, 110)
  const c = { x: clamp(ctx.W * 0.5, ax + m, ctx.W - ax - m), y: clamp(Math.max(ctx.home.y + fish.height * 2.5, ctx.H * 0.4), ay + m, ctx.H - ay - m) }
  const ignite = 950, fizzle = 2000
  const theta = (ms: number) => 2 * Math.PI * easeInOut(seg(ms, ignite, fizzle))
  const out = (ms: number) => easeInOut(seg(ms, 0, 450)) * (1 - easeInOut(seg(ms, 2550, 3000)))
  const fishPose = (ms: number): Pose => {
    const o = out(ms), scale = lerp(1, big, o), t = theta(ms)
    const flying = seg(ms, ignite, ignite + 100) * (1 - seg(ms, fizzle - 50, fizzle + 50))
    const dx = ax * Math.cos(t), dy = 2 * ay * Math.cos(2 * t)
    const f = flying > 0 ? Math.tanh(dx / (ax * 0.3 + 1)) : 1
    const face = Math.abs(f) < 0.25 ? Math.sign(f || 1) * 0.25 : f
    const drift = 25 * ctx.k * easeOut(seg(ms, fizzle + 50, 2550))
    const sway = 8 * Math.sin(ms / 90) * seg(ms, fizzle + 50, 2200) * (1 - seg(ms, 2450, 2550))
    const jitter = 2 * ctx.k * Math.sin(ms / 20) * bump(ms, 550, ignite)
    const p = { x: c.x + ax * Math.sin(t) + jitter, y: c.y + ay * Math.sin(2 * t) + drift }
    return pose(lerp(ctx.home.x, p.x, o), lerp(ctx.home.y, p.y, o), {
      rotate: flying * clamp(deg(Math.atan2(dy, Math.abs(dx))), -60, 60) * Math.sign(face) + sway, scaleX: scale * (flying > 0 ? face : 1), scaleY: scale,
    })
  }
  fishSprites(ctx, fishPose, ms => seg(ms, fizzle, fizzle + 50) - seg(ms, 2250, 2300))
  const strapped = (ms: number) => seg(ms, 380, 450) * (1 - seg(ms, 2450, 2600))
  const local = { x: -1, y: -6.2 }, nozzle = { x: -7.2, y: -6.2 }
  ctx.sprites.push({ id: 'rocket', kind: { type: 'rocket' }, width: fish.width * 0.5, height: fish.width * 0.17, pose: ms => {
    if (strapped(ms) <= 0) return HIDDEN
    const f = fishPose(ms), p = fishPoint(f, ctx.unit, local), grow = easeOutBack(seg(ms, 380, 520))
    return pose(p.x, p.y, { rotate: f.rotate, scaleX: f.scaleX * grow, scaleY: Math.abs(f.scaleY) * grow, opacity: strapped(ms) })
  } })
  const exhaust = (ms: number) => fishPoint(fishPose(ms), ctx.unit, nozzle)
  ctx.sprites.push({ id: 'flame', kind: { type: 'flash' }, width: 16 * ctx.k, height: 16 * ctx.k, pose: ms => {
    if (ms <= ignite || ms >= fizzle) return HIDDEN
    const p = exhaust(ms), flicker = 0.8 + 0.5 * noise(Math.floor(ms / 40))
    return pose(p.x, p.y, { scaleX: flicker, scaleY: flicker, rotate: ms / 3 })
  } })
  // A smoke trail left hanging along the loop.
  for (let n = 0; n < 10; n++) {
    const born = ignite + 60 + n * 95, size = (8 + 4 * noise(n)) * ctx.k
    ctx.sprites.push({ id: `trail${n}`, kind: { type: 'smoke' }, width: size, height: size, pose: ms => {
      const s = seg(ms, born, born + 520)
      if (s <= 0 || s >= 1) return HIDDEN
      const p = exhaust(born)
      return pose(p.x, p.y - 6 * ctx.k * s, { opacity: 0.6 * (1 - s), scaleX: 0.6 + s, scaleY: 0.6 + s })
    } })
  }
  for (const at of [600, 800]) puffs(ctx, `sputter${at}-`, 3, () => exhaust(at), at, 9 * ctx.k, 7)
  puffs(ctx, 'fizzle', 4, () => exhaust(fizzle), fizzle, 12 * ctx.k, 8)
  const h = 22 * ctx.k
  speech(ctx, 'pfft', 'pfft…', h, ms => { const p = fishPose(ms); return { x: p.x + h * 2.2, y: p.y - fish.height * 0.4 } }, fizzle + 20, 2450)
  const chuteW = fish.width * 1.3, chuteH = chuteW * 0.8
  ctx.sprites.push({ id: 'parachute', kind: { type: 'parachute' }, width: chuteW, height: chuteH, pose: ms => {
    if (ms <= fizzle + 50 || ms >= 2600) return HIDDEN
    const f = fishPose(ms), open = easeOutBack(seg(ms, fizzle + 50, fizzle + 250))
    return pose(f.x, f.y - fish.height * big * 0.25 - chuteH * 0.45, { scaleX: open, scaleY: 0.4 + 0.6 * open, rotate: 0.6 * (f.rotate), opacity: 1 - seg(ms, 2450, 2600) })
  } })
  return duration
}

/** Balloon: it puffs itself up, floats there, then lets go and zips about squealing as the air rushes out. */
function balloon(ctx: Ctx) {
  const duration = LOGO_SCENE_DURATIONS.balloon
  const { fish } = ctx.layout
  const max = 2.1, half = fish.width * max / 2, tall = fish.height * max * 1.25 / 2
  const spot = { x: clamp(ctx.home.x + fish.width * 1.6, half + 6, ctx.W - half - 6), y: clamp(ctx.home.y + fish.height * 2.2, tall + 6, ctx.H - tall - 6) }
  const leak = 1200
  const ampX = clamp(Math.min(spot.x - fish.width * 0.8, ctx.W - spot.x - fish.width * 0.8), 10, 150)
  const ampY = clamp(Math.min(spot.y - fish.height, ctx.H - spot.y - fish.height), 10, 70)
  const fishPose = (ms: number): Pose => {
    const out = easeInOut(seg(ms, 0, 400)) * (1 - easeInOut(seg(ms, 2150, 2600)))
    const inflate = easeOut(seg(ms, 400, 1100)) * (1 - easeOut(seg(ms, leak, leak + 350)))
    const zip = seg(ms, leak, 1950), z = Math.sin(Math.PI * zip) * (1 - zip)
    const x = spot.x + ampX * Math.sin(zip * 17) * z, y = spot.y + ampY * Math.sin(zip * 23) * z - 6 * ctx.k * Math.sin(ms / 200) * inflate
    const size = lerp(1, 1.2, out) + (max - 1.2) * inflate
    return pose(lerp(ctx.home.x, x, out), lerp(ctx.home.y, y, out), {
      rotate: 720 * easeOut(zip) + 5 * Math.sin(ms / 160) * inflate, scaleX: size, scaleY: size * (1 + 0.25 * inflate),
    })
  }
  fishSprites(ctx, fishPose, ms => seg(ms, 700, 750) - seg(ms, 1100, 1150) + seg(ms, 1950, 2000) - seg(ms, 2150, 2200))
  // The air rushing out of its mouth.
  for (let n = 0; n < 9; n++) {
    const born = leak + 30 + n * 75, size = (7 + 4 * noise(n + 1)) * ctx.k
    ctx.sprites.push({ id: `air${n}`, kind: { type: 'smoke' }, width: size, height: size, pose: ms => {
      const s = seg(ms, born, born + 400)
      if (s <= 0 || s >= 1) return HIDDEN
      const p = fishPoint(fishPose(born), ctx.unit, FISH_POINTS.mouth)
      return pose(p.x, p.y, { opacity: 0.6 * (1 - s), scaleX: 0.5 + s, scaleY: 0.5 + s })
    } })
  }
  const h = 26 * ctx.k
  speech(ctx, 'squeal', 'PFFFFRRT!', h, () => ({ x: spot.x, y: spot.y - tall - h * 0.2 }), leak + 50, 1900)
  dizzy(ctx, 'dizzy', fishPose, 1950, 2300)
  return duration
}

/** Disguise: on go the glasses, nose and moustache; it glances about ("NOT A CARP"), then they fall off. */
function disguise(ctx: Ctx) {
  const duration = LOGO_SCENE_DURATIONS.disguise
  const { fish } = ctx.layout
  const drop = fish.height * 1.3
  const fishPose = (ms: number): Pose => {
    const pop = popOut(ctx, ms, 0.45, 0, 300, duration)
    const look = seg(ms, 750, 1650)
    const f = Math.cos(4 * Math.PI * look)
    const face = Math.abs(f) < 0.15 ? Math.sign(f || 1) * 0.15 : f
    const shrug = bump(ms, 1850, 2100)
    return pose(ctx.home.x + pop.dx + 8 * ctx.k * Math.sin(2 * Math.PI * look), ctx.home.y + pop.dy + drop * pop.p - 4 * ctx.k * shrug, {
      rotate: -4 * pop.p + 8 * shrug, scaleX: pop.scale * face, scaleY: pop.scale * (1 + 0.05 * shrug),
    })
  }
  fishSprites(ctx, fishPose, ms => seg(ms, 1850, 1890) - seg(ms, 2050, 2090))
  const w = 9 * ctx.unit, h = 7.2 * ctx.unit
  const worn = (ms: number) => {
    const f = fishPose(ms), p = fishPoint(f, ctx.unit, { x: FISH_POINTS.eye.x + 1.4, y: FISH_POINTS.eye.y + 1.6 })
    return pose(p.x, p.y, { rotate: f.rotate, scaleX: f.scaleX, scaleY: f.scaleY })
  }
  const fall = worn(1800)
  ctx.sprites.push({ id: 'disguise', kind: { type: 'disguise' }, width: w, height: h, pose: ms => {
    if (ms <= 350 || ms >= 2250) return HIDDEN
    if (ms < 1800) {
      const p = worn(ms), s = easeOut(seg(ms, 350, 560))
      return { ...p, y: p.y - 40 * ctx.k * (1 - s), opacity: seg(ms, 350, 420) }
    }
    const t = (ms - 1800) / 1000
    return { ...fall, x: fall.x + 30 * ctx.k * t, y: fall.y + 500 * ctx.k * t * t, rotate: fall.rotate + 300 * t, opacity: 1 - seg(ms, 2050, 2250) }
  } })
  const sh = 24 * ctx.k
  speech(ctx, 'not-a-carp', 'NOT A CARP', sh, ms => { const p = fishPose(ms); return { x: p.x + fish.width * 1.2, y: p.y - fish.height * 0.9 } }, 800, 1700)
  spark(ctx, 'oops', 12 * ctx.k, '#f2c14e', () => ({ x: fall.x, y: fall.y - fish.height * 0.8 }), 1800, 2050, 90)
  return duration
}

/** Weightlifting: it hoists a barbell overhead, trembling ("GAINZ!"), and the barbell flattens it like a pancake. */
function lift(ctx: Ctx) {
  const duration = LOGO_SCENE_DURATIONS.lift
  const { fish } = ctx.layout
  const drop = fish.height * 1.7
  const up = 850, top = 1250, slam = 1700, back = 2050
  const squash = (ms: number) => easeOut(seg(ms, slam, slam + 80)) * (1 - easeOutBack(seg(ms, back, back + 300)))
  // Steps out far enough from the edge that the pancake (1.6x wide) stays on screen.
  const room = Math.max(0, fish.width * 1.1 + 4 - ctx.home.x)
  const base = (ms: number) => { const pop = popOut(ctx, ms, 0.35, 0, 300, duration); return { pop, x: ctx.home.x + pop.dx + room * pop.p, y: ctx.home.y + pop.dy + drop * pop.p } }
  const fishPose = (ms: number): Pose => {
    const { pop, x, y } = base(ms), sq = squash(ms)
    const strain = Math.sin(ms / 25) * bump(ms, 550, slam)
    return pose(x + 1.5 * ctx.k * strain, y + fish.height * pop.scale * 0.3 * sq, {
      rotate: 3 * strain, scaleX: pop.scale * (1 + 0.6 * sq), scaleY: pop.scale * (1 - 0.6 * sq - 0.06 * bump(ms, up, top)),
    })
  }
  fishSprites(ctx, fishPose, ms => seg(ms, slam, slam + 40) - seg(ms, back + 100, back + 140))
  const barW = fish.width * 1.6, barH = fish.height * 0.55
  ctx.sprites.push({ id: 'barbell', kind: { type: 'barbell' }, width: barW, height: barH, pose: ms => {
    if (ms <= 350 || ms >= 2350) return HIDDEN
    const { pop, x, y } = base(ms)
    const floor = y + fish.height * pop.scale * 0.55 + barH * 0.2, overhead = y - fish.height * pop.scale * 0.6 - barH * 0.2
    const raised = lerp(floor, overhead, easeInOut(seg(ms, up, top)))
    const at = ms < slam - 80 ? raised : lerp(overhead, y + fish.height * 0.1, easeIn(seg(ms, slam - 80, slam)))
    // Once it pops back into shape, the barbell rolls away.
    const roll = seg(ms, back, 2350)
    return pose(x + 40 * ctx.k * roll, at + (ms > back ? (floor - at) * easeOut(roll) : 0), {
      rotate: 6 * Math.sin(ms / 40) * bump(ms, top, slam - 80) + 120 * roll, opacity: seg(ms, 350, 450) * (1 - seg(roll, 0.6, 1)),
    })
  } })
  puffs(ctx, 'thud', 4, () => { const { x, y } = base(slam); return { x, y: y + fish.height * 0.5 } }, slam, 18 * ctx.k, 9)
  const h = 24 * ctx.k
  speech(ctx, 'gainz', 'GAINZ!', h, ms => { const p = fishPose(ms); return { x: p.x + fish.width * 1.1, y: p.y - fish.height * 0.6 } }, top - 50, slam - 100)
  dizzy(ctx, 'dizzy', fishPose, slam + 100, back + 250)
  return duration
}

/**
 * The helpful paperclip: an office-assistant paperclip pops up at the bottom
 * of the screen offering help with conquering the world. The carp stares at
 * it a while, then lobs a grenade. Cartoon boom, a bent wire bounces away.
 */
function paperclip(ctx: Ctx) {
  const duration = LOGO_SCENE_DURATIONS.paperclip
  const { fish } = ctx.layout
  const clipW = 52 * ctx.k, clipH = 76 * ctx.k
  const clip = { x: clamp(ctx.W * 0.66, clipW / 2 + 8, ctx.W - clipW / 2 - 8), y: ctx.H - clipH / 2 - 10 }
  const throwAt = 2050, boom = 2550
  const fishPose = (ms: number): Pose => {
    const pop = popOut(ctx, ms, 0.45, 250, 300, duration)
    const stare = seg(ms, 600, 800) * (1 - seg(ms, throwAt + 300, throwAt + 500))
    const windup = bump(ms, throwAt - 200, throwAt + 120)
    const hop = bump(ms, boom + 150, boom + 400)
    return pose(ctx.home.x + pop.dx, ctx.home.y + pop.dy + fish.height * 1.2 * pop.p - 8 * ctx.k * hop, {
      rotate: 16 * stare - 30 * windup + 8 * bump(ms, throwAt + 50, throwAt + 300), scaleX: pop.scale, scaleY: pop.scale * (1 + 0.06 * hop),
    })
  }
  // Two slow blinks while it stares.
  fishSprites(ctx, fishPose, ms => seg(ms, 1100, 1140) - seg(ms, 1220, 1260) + seg(ms, 1600, 1640) - seg(ms, 1720, 1760))
  ctx.sprites.push({ id: 'paperclip', kind: { type: 'paperclip' }, width: clipW, height: clipH, pose: ms => {
    if (ms <= 0 || ms >= boom) return HIDDEN
    const up = easeOutBack(seg(ms, 0, 350))
    // A cheery bounce, then the raised-eyebrow head tilt while the carp stares back.
    const tilt = -9 * easeInOut(seg(ms, 900, 1150)) * (1 - easeInOut(seg(ms, 1900, 2100))) + 4 * Math.sin(ms / 140) * seg(ms, 350, 500) * (1 - seg(ms, 850, 900))
    const flinch = bump(ms, boom - 250, boom)
    return pose(clip.x, clip.y + clipH * 0.5 * (1 - up), { scaleX: up * (1 - 0.1 * flinch), scaleY: up * (1 + 0.1 * flinch), rotate: tilt + 6 * Math.sin(ms / 25) * flinch, opacity: seg(ms, 0, 60) })
  } })
  const tipW = clamp(ctx.W * 0.62, 180, 290), tipH = tipW * 0.3
  const tip = { x: clamp(clip.x - tipW * 0.2, tipW / 2 + 6, ctx.W - tipW / 2 - 6), y: clip.y - clipH * 0.5 - tipH * 0.55 - 6 }
  ctx.sprites.push({ id: 'tooltip', kind: { type: 'text', text: "It looks like you're trying to conquer the world. Would you like help?", style: 'tip' }, width: tipW, height: tipH, pose: ms => {
    if (ms <= 400 || ms >= 2000) return HIDDEN
    const grow = easeOutBack(seg(ms, 400, 600)) * (1 - easeIn(seg(ms, 1850, 2000)))
    return pose(tip.x, tip.y, { scaleX: grow, scaleY: grow })
  } })
  // The grenade: out of nowhere, pin out, lobbed in a high arc.
  const size = 15 * ctx.k
  const hand = (ms: number) => fishPoint(fishPose(ms), ctx.unit, { x: FISH_POINTS.mouth.x + 1, y: FISH_POINTS.mouth.y - 2 })
  const from = hand(throwAt), arc = Math.min(70 * ctx.k, from.y - size)
  ctx.sprites.push({ id: 'grenade', kind: { type: 'grenade' }, width: size, height: size * 1.2, pose: ms => {
    if (ms <= throwAt - 250 || ms >= boom) return HIDDEN
    if (ms < throwAt) { const h = hand(ms); return pose(h.x, h.y, { scaleX: easeOutBack(seg(ms, throwAt - 250, throwAt - 120)), scaleY: easeOutBack(seg(ms, throwAt - 250, throwAt - 120)) }) }
    const s = seg(ms, throwAt, boom)
    return pose(lerp(from.x, clip.x, s), Math.max(size, lerp(from.y, clip.y, easeIn(s)) - arc * Math.sin(Math.PI * s)), { rotate: 600 * s })
  } })
  spark(ctx, 'pin', 8 * ctx.k, '#c9ced6', () => hand(throwAt - 120), throwAt - 140, throwAt + 100, 200)
  // Boom: a big flash, a smoke cloud, the stray wire bouncing off.
  ctx.sprites.push({ id: 'kaboom', kind: { type: 'flash' }, width: 70 * ctx.k, height: 70 * ctx.k, pose: ms => {
    const s = seg(ms, boom, boom + 260)
    if (s <= 0 || s >= 1) return HIDDEN
    return pose(clamp(clip.x, 58 * ctx.k, ctx.W - 58 * ctx.k), Math.min(clip.y, ctx.H - 58 * ctx.k), { scaleX: 0.5 + 0.7 * s, scaleY: 0.5 + 0.7 * s, opacity: 1 - easeIn(s), rotate: 25 * s })
  } })
  puffs(ctx, 'smoke', 7, () => ({ x: clip.x, y: clip.y - 6 * ctx.k }), boom + 40, 30 * ctx.k, 16)
  const wireW = 22 * ctx.k, floor = ctx.H - wireW * 0.3 - 6
  ctx.sprites.push({ id: 'wire', kind: { type: 'wire' }, width: wireW, height: wireW * 0.5, pose: ms => {
    const t = (ms - boom) / 1000
    if (t <= 0.02 || t >= 0.85) return HIDDEN
    // Up and out, then two bounces along the bottom.
    const x = clamp(clip.x + 150 * ctx.k * t, wireW / 2, ctx.W - wireW / 2)
    const fly = Math.min(1, t / 0.45), first = Math.max(0, Math.min(clip.y - 80 * ctx.k * Math.sin(Math.PI * fly), floor))
    const bounce = t > 0.45 ? floor - 22 * ctx.k * Math.abs(Math.sin(Math.PI * (t - 0.45) / 0.2)) * (1 - (t - 0.45) / 0.4) : first
    return pose(x, Math.max(wireW / 2, t > 0.45 ? bounce : first), { rotate: 720 * t, opacity: 1 - seg(t, 0.65, 0.85) })
  } })
  const h = 30 * ctx.k
  speech(ctx, 'boom', 'KA-BOOM!', h, () => ({ x: clip.x - h, y: clip.y - clipH * 0.6 }), boom + 30, boom + 750)
  return duration
}

export function buildLogoScene(reaction: LogoReaction, layout: SceneLayout, { enemies = [], reducedMotion = false, seed = 0 }: SceneOptions = {}): LogoScene {
  const { fish, stage } = layout
  const ctx: Ctx = {
    layout, sprites: [], W: stage.width, H: stage.height,
    home: { x: fish.x + fish.width / 2, y: fish.y + fish.height / 2 },
    unit: fish.height / FISH_VIEWBOX.height, k: clamp(fish.height / 36, 0.8, 1.5), rng: seededRandom(seed),
    band: shotBand(layout, shotTier(seed)),
  }
  const play = reducedMotion ? 'wink' : reaction
  const duration = play === 'fire-double' ? fireDouble(ctx, enemies.length >= 2 ? enemies : LOGO_FIRE_TARGETS['fire-double'])
    : play === 'fire-miss' ? fireMiss(ctx, enemies[0] ?? LOGO_FIRE_TARGETS['fire-miss'][0])
    : play === 'fire-combo' ? fireCombo(ctx, (enemies.length >= 2 ? enemies : LOGO_FIRE_TARGETS['fire-combo']).slice(0, 3))
    : isFireReaction(play) ? fireAt(ctx, enemies[0] ?? LOGO_FIRE_TARGETS[play][0])
    : play === 'invasion' ? invasion(ctx, enemies.length >= 3 ? enemies : ['puffer', 'jelly', 'shark'])
    : play === 'wink' ? wink(ctx, reducedMotion)
    : play === 'flip' ? flip(ctx)
    : play === 'swim' ? swim(ctx)
    : play === 'bubbles' ? bubbles(ctx)
    : play === 'salute' ? salute(ctx)
    : play === 'shake' ? shake(ctx)
    : play === 'war' ? war(ctx)
    : play === 'dance' ? dance(ctx)
    : play === 'hula' ? hula(ctx)
    : play === 'juggle' ? juggle(ctx)
    : play === 'magic' ? magic(ctx)
    : play === 'skate' ? skate(ctx)
    : play === 'sneeze' ? sneeze(ctx)
    : play === 'duel' ? duel(ctx)
    : play === 'waltz' ? waltz(ctx)
    : play === 'rocket' ? rocket(ctx)
    : play === 'balloon' ? balloon(ctx)
    : play === 'disguise' ? disguise(ctx)
    : play === 'lift' ? lift(ctx)
    : play === 'paperclip' ? paperclip(ctx)
    : chase(ctx)
  return { reaction: play, duration, sprites: ctx.sprites }
}

export interface SampledTrack { input: number[]; output: number[] }
export type SampledPose = Record<keyof Pose, SampledTrack | number>

/**
 * Samples a sprite's pose at 60 Hz (progress 0..1) and drops points that a
 * straight line through their neighbours already reproduces, so the native
 * interpolation tables stay small. A constant property becomes a number.
 */
export function sampleSprite(sprite: SceneSprite, duration: number, hz = 60): SampledPose {
  const count = Math.max(2, Math.ceil(duration / 1000 * hz))
  const times = Array.from({ length: count + 1 }, (_, n) => n / count)
  const poses = times.map(t => sprite.pose(t * duration))
  const keys: (keyof Pose)[] = ['x', 'y', 'rotate', 'scaleX', 'scaleY', 'opacity']
  const result = {} as SampledPose
  for (const key of keys) {
    const values = poses.map(p => p[key])
    if (values.every(v => Math.abs(v - values[0]!) < 1e-6)) { result[key] = values[0]!; continue }
    const tolerance = key === 'opacity' || key === 'scaleX' || key === 'scaleY' ? 0.004 : key === 'rotate' ? 0.15 : 0.15
    const input = [times[0]!], output = [values[0]!]
    let anchor = 0
    for (let n = 1; n < values.length - 1; n++) {
      // Keep n if skipping it would bend any sample between the anchor and n+1 out of tolerance.
      const t0 = times[anchor]!, v0 = values[anchor]!, t1 = times[n + 1]!, v1 = values[n + 1]!
      let keep = false
      for (let m = anchor + 1; m <= n; m++) {
        const expected = v0 + (v1 - v0) * (times[m]! - t0) / (t1 - t0)
        if (Math.abs(expected - values[m]!) > tolerance) { keep = true; break }
      }
      if (keep) { input.push(times[n]!); output.push(values[n]!); anchor = n }
    }
    input.push(times[values.length - 1]!); output.push(values[values.length - 1]!)
    result[key] = { input, output }
  }
  return result
}
